import { DEFAULT_PORT, UNFOUND_INDEX } from '../constants';
import {
	copyFileSync,
	cpSync,
	existsSync,
	mkdirSync,
	readdirSync,
	readFileSync,
	renameSync,
	rmSync,
	statSync,
	writeFileSync
} from 'node:fs';
import { basename, dirname, extname, join, relative, resolve } from 'node:path';
import { cwd, env, exit } from 'node:process';
import { build as bunBuild, type BuildArtifact, Glob } from 'bun';
import { generateManifest } from '../build/generateManifest';
import { verifyAngularCoreUniqueness } from '../build/verifyAngularCoreUniqueness';
import {
	collectIslandFrameworkSources,
	generateIslandEntryPoints,
	loadIslandRegistryBuildInfo
} from '../build/islandEntries';
import { generateReactIndexFiles } from '../build/generateReactIndexes';
import { createHTMLScriptHMRPlugin } from '../build/htmlScriptHMRPlugin';
import { transformStaticPagesWithIslands } from '../build/staticIslandPages';
import { outputLogs } from '../build/outputLogs';
import { scanEntryPoints } from '../build/scanEntryPoints';
import { scanConventions } from '../build/scanConventions';
import { writeSpaSideManifests } from '../build/spaSideManifests';
import { buildServerBundleExternals } from '../build/serverExternals';
import { scanRouteRegistrations } from '../build/scanRouteRegistrations';
import { generateSitemap } from '../utils/generateSitemap';
import type {
	ConventionsMap,
	FrameworkConventionEntry,
	FrameworkConventions,
	PageConventions
} from '../../types/conventions';
import { scanCssEntryPoints } from '../build/scanCssEntryPoints';
import {
	createStyleTransformConfig,
	createStylePreprocessorPlugin,
	isStylePath
} from '../build/stylePreprocessor';
import {
	compileTailwindConfig,
	computeFrameworkTailwindSources,
	isTailwindCandidate
} from '../build/compileTailwind';
import { disposeTailwindCompiler } from '../build/tailwindCompiler';
import { optimizeHtmlImages } from '../build/optimizeHtmlImages';
import { updateAssetPaths } from '../build/updateAssetPaths';
import { buildHMRClient } from '../dev/buildHMRClient';
import {
	patchRefreshGlobals,
	rewriteReactImports
} from '../build/rewriteReactImports';
import { maskLiterals } from '../build/maskLiterals';
import { sendTelemetryEvent } from '../cli/telemetryEvent';
import {
	getAngularServerVendorPaths,
	getAngularVendorPaths,
	getDevVendorPaths,
	getSvelteVendorPaths,
	getVueVendorPaths,
	setAngularServerVendorPaths,
	setAngularVendorPaths,
	setDevVendorPaths,
	setSvelteVendorPaths,
	setVueVendorPaths
} from './devVendorPaths';
import type {
	BuildConfig,
	BuildPassError,
	BunBuildConfigOverride,
	BunBuildPassConfig,
	BunBuildPassKey
} from '../../types/build';
import { createAngularLinkerPlugin } from '../build/angularLinkerPlugin';
import { createExternalAssetPlugin } from '../build/externalAssetPlugin';
import { createIslandRegistryDefinitionPlugin } from '../build/islandRegistryTransform';
import { createAngularHmrInjectionPlugin } from '../dev/angular/hmrInjectionPlugin';
import { cleanStaleOutputs } from '../utils/cleanStaleOutputs';
import { cleanup } from '../utils/cleanup';
import { commonAncestor } from '../utils/commonAncestor';
import {
	getFrameworkGeneratedDir,
	getGeneratedRoot
} from '../utils/generatedDir';
import { withBuildDirectoryLock } from '../utils/buildDirectoryLock';
import { logError, logWarn } from '../utils/logger';
import { normalizePath } from '../utils/normalizePath';
import { toKebab, toPascal } from '../utils/stringModifiers';
import { validateSafePath } from '../utils/validateSafePath';
import { setSpaRouteManifest } from '../utils/spaRouteManifest';

const isDev = env.NODE_ENV === 'development';

type BuildTraceEvent = {
	durationMs: number;
	metadata?: Record<string, unknown>;
	name: string;
	ok: boolean;
	startMs: number;
};

type BuildTracePhase = <T>(
	name: string,
	fn: () => Promise<T> | T,
	metadata?: Record<string, unknown>
) => Promise<T>;

const isBuildTraceEnabled = () => {
	const value = env.ABSOLUTE_BUILD_TRACE?.toLowerCase();

	return value === '1' || value === 'true' || value === 'yes';
};

const collectConventionSourceFiles = (
	entry: FrameworkConventionEntry | undefined
) => {
	if (!entry) return [];
	const files: string[] = [];
	if (entry.defaults?.error) files.push(entry.defaults.error);
	if (entry.defaults?.loading) files.push(entry.defaults.loading);
	if (entry.defaults?.notFound) files.push(entry.defaults.notFound);
	if (!entry.pages) return files;
	for (const page of Object.values(entry.pages)) {
		if (page.error) files.push(page.error);
		if (page.loading) files.push(page.loading);
	}

	return files;
};

const updateConventionCompiledPaths = (
	entry: FrameworkConventionEntry | undefined,
	sourcePaths: string[],
	compiledPaths: string[]
) => {
	if (!entry || sourcePaths.length !== compiledPaths.length) return;
	const pathMap = new Map<string, string>();
	for (let idx = 0; idx < sourcePaths.length; idx++) {
		const src = sourcePaths[idx];
		const compiled = compiledPaths[idx];
		if (src && compiled) pathMap.set(src, compiled);
	}

	if (entry.defaults) {
		const errorPath = entry.defaults.error
			? pathMap.get(entry.defaults.error)
			: undefined;
		if (errorPath) entry.defaults.error = errorPath;

		const loadingPath = entry.defaults.loading
			? pathMap.get(entry.defaults.loading)
			: undefined;
		if (loadingPath) entry.defaults.loading = loadingPath;

		const notFoundPath = entry.defaults.notFound
			? pathMap.get(entry.defaults.notFound)
			: undefined;
		if (notFoundPath) entry.defaults.notFound = notFoundPath;
	}
	if (!entry.pages) return;
	for (const page of Object.values(entry.pages)) {
		const errorPath = page.error ? pathMap.get(page.error) : undefined;
		if (errorPath) page.error = errorPath;

		const loadingPath = page.loading
			? pathMap.get(page.loading)
			: undefined;
		if (loadingPath) page.loading = loadingPath;
	}
};

/* Turn a failing pass's Bun logs into a specific, actionable message.
 * A bare Bun resolve failure (`Could not resolve: "./reset.css"`) omits
 * WHERE the reference lives, so we fold in the referrer file + the
 * unresolved specifier + line when Bun provides them. Also returns the
 * structured bits so callers can drive the dev error overlay. */
const summarizeBuildFailure = (
	logs: (BuildMessage | ResolveMessage)[],
	label: string
) => {
	const errLog = logs.find((log) => log.level === 'error') ?? logs[0];
	const baseMessage = errLog?.message ?? `${label} build failed`;

	// `specifier` only exists on ResolveMessage; `position.file` is the
	// referrer for both message kinds. Narrow via `in` — no assertion.
	const hasSpecifier =
		errLog !== undefined &&
		'specifier' in errLog &&
		typeof errLog.specifier === 'string' &&
		errLog.specifier.length > 0;
	const specifier = hasSpecifier ? errLog.specifier : undefined;
	const position = errLog?.position ?? undefined;
	const file = position?.file || undefined;
	const line = position?.line || undefined;
	const column = position?.column || undefined;

	const detailLines: string[] = [baseMessage];
	if (specifier) {
		detailLines.push(`  Unresolved reference: '${specifier}'`);
	}
	if (file) {
		detailLines.push(`  Referenced from: ${file}${line ? `:${line}` : ''}`);
	}

	return {
		column,
		file,
		line,
		message: detailLines.join('\n'),
		specifier
	};
};

// Bare React vendor specifiers that must never survive in a browser chunk —
// the browser can't resolve them ("Failed to resolve module specifier
// 'react/jsx-dev-runtime'") and the app never hydrates. Longest first so the
// alternation matches `react-dom/client` before `react-dom` before `react`.
const REACT_VENDOR_SPECIFIERS = [
	'react-dom/client',
	'react-refresh/runtime',
	'react/jsx-dev-runtime',
	'react/jsx-runtime',
	'react-dom',
	'react'
];

// Bare React vendor specifiers present in a single browser chunk's text, as
// real imports (not text). Scans the MASKED source so specifiers appearing
// inside strings/templates/comments (React's dev error messages, rendered code
// samples) aren't mistaken for imports.
const findBareReactImports = (path: string, importRegex: RegExp) => {
	let content: string;
	try {
		content = readFileSync(path, 'utf-8');
	} catch {
		return [];
	}
	if (!content.includes('react')) return [];
	const { masked } = maskLiterals(content);
	const specs = [...masked.matchAll(importRegex)]
		.map((match) => match[1])
		.filter((spec): spec is string => Boolean(spec));

	return [...new Set(specs)];
};

/* Production guardrail: after every client rewrite, verify no emitted BROWSER
 * chunk still imports a bare React vendor specifier. A single un-vendored
 * specifier in one split/shared chunk (e.g. from a mask-tokenizer desync)
 * silently breaks hydration at runtime; failing the build makes that
 * impossible to ship. Not run for HMR/dev builds — those serve modules through
 * the dev server, not as static browser chunks. */
const verifyBrowserImportsVendored = (outputPaths: string[]) => {
	const alt = REACT_VENDOR_SPECIFIERS.map((spec) =>
		spec.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
	).join('|');
	const importRegex = new RegExp(
		`(?:from|import|require)\\s*\\(?\\s*["'](${alt})["']`,
		'g'
	);
	const seenPaths = new Set<string>();
	const offenders: string[] = [];
	for (const path of outputPaths) {
		if (!path.endsWith('.js') || seenPaths.has(path)) continue;
		seenPaths.add(path);
		const specs = findBareReactImports(path, importRegex);
		if (specs.length > 0) offenders.push(`${path} → ${specs.join(', ')}`);
	}
	if (offenders.length === 0) return;
	throw new Error(
		`Build emitted browser chunk(s) with un-vendored bare React ` +
			`specifiers — these fail to resolve in the browser and break ` +
			`hydration:\n${offenders.map((o) => `  • ${o}`).join('\n')}`
	);
};

const buildPassError = (
	logs: (BuildMessage | ResolveMessage)[],
	pass: string,
	label: string,
	frameworkNames: string[],
	isIncremental: boolean | 0 | undefined
) => {
	const { column, file, line, message, specifier } = summarizeBuildFailure(
		logs,
		label
	);
	const err = new Error(message);
	Object.assign(err, { logs });
	sendTelemetryEvent('build:error', {
		frameworks: frameworkNames,
		incremental: Boolean(isIncremental),
		message,
		pass
	});
	logError(`${label} build failed`, err);

	return { column, file, label, line, logs, message, pass, specifier };
};

const extractBuildError = (
	logs: (BuildMessage | ResolveMessage)[],
	pass: string,
	label: string,
	frameworkNames: string[],
	isIncremental: boolean | 0 | undefined,
	throwOnError: boolean
) => {
	const passError = buildPassError(
		logs,
		pass,
		label,
		frameworkNames,
		isIncremental
	);
	if (throwOnError) {
		const err = new Error(passError.message);
		Object.assign(err, { logs });
		throw err;
	}
	exit(1);
};

const copyHtmxVendor = (htmxDir: string, htmxDestDir: string) => {
	mkdirSync(htmxDestDir, { recursive: true });
	const glob = new Glob('htmx*.min.js');
	for (const relPath of glob.scanSync({ cwd: htmxDir })) {
		const src = join(htmxDir, relPath);
		const dest = join(htmxDestDir, 'htmx.min.js');
		copyFileSync(src, dest);

		return;
	}
};

const tryReadPackageJson = async (path: string) => {
	try {
		return await Bun.file(path).json();
	} catch {
		return null;
	}
};

const resolveAbsoluteVersion = async () => {
	const candidates = [
		resolve(import.meta.dir, '..', '..', 'package.json'),
		resolve(import.meta.dir, '..', 'package.json')
	];
	const resolveCandidate = async (remaining: string[]) => {
		const [candidate, ...rest] = remaining;
		if (!candidate) {
			return;
		}

		const pkg = await tryReadPackageJson(candidate);
		if (!pkg || pkg.name !== '@absolutejs/absolute') {
			await resolveCandidate(rest);

			return;
		}

		globalThis.__absoluteVersion = pkg.version;
	};

	await resolveCandidate(candidates);
};

/** Scan source directories for files referenced by new URL('./path', import.meta.url) */
const SKIP_DIRS = new Set([
	'build',
	'node_modules',
	'.absolutejs',
	'generated'
]);
const addWorkerPathIfExists = (
	file: string,
	relPath: string,
	workerPaths: Set<string>
) => {
	const absPath = resolve(file, '..', relPath);
	try {
		statSync(absPath);
		workerPaths.add(absPath);
	} catch {
		// Referenced file doesn't exist, skip
	}
};

const collectWorkerPathsFromContent = (
	content: string,
	pattern: RegExp,
	file: string,
	workerPaths: Set<string>
) => {
	pattern.lastIndex = 0;
	let match;
	while ((match = pattern.exec(content)) !== null) {
		const [, relPath] = match;
		if (!relPath) continue;
		addWorkerPathIfExists(file, relPath, workerPaths);
	}
};

const collectWorkerPathsFromFile = (
	file: string,
	patterns: RegExp[],
	workerPaths: Set<string>
) => {
	const content = readFileSync(file, 'utf-8');
	for (const pattern of patterns) {
		collectWorkerPathsFromContent(content, pattern, file, workerPaths);
	}
};

const scanWorkerReferencesInDir = async (
	dir: string,
	patterns: RegExp[],
	workerPaths: Set<string>
) => {
	const glob = new Glob('**/*.{ts,tsx,js,jsx,svelte,vue}');
	for await (const file of glob.scan({ absolute: true, cwd: dir })) {
		// Skip build-generated directories
		const relToDir = file.slice(dir.length + 1);
		const [firstSegment] = relToDir.split('/');
		if (firstSegment && SKIP_DIRS.has(firstSegment)) continue;

		collectWorkerPathsFromFile(file, patterns, workerPaths);
	}
};

const scanWorkerReferences = async (dirs: string[]) => {
	const urlPattern =
		/new\s+URL\(\s*["'](\.\.?\/[^"']+)["']\s*,\s*import\.meta\.url\s*\)/g;
	const resolvePattern =
		/import\.meta\.resolve\(\s*["'](\.\.?\/[^"']+)["']\s*\)/g;
	const workerPaths = new Set<string>();

	await Promise.all(
		dirs.map((dir) =>
			scanWorkerReferencesInDir(
				dir,
				[urlPattern, resolvePattern],
				workerPaths
			)
		)
	);

	return [...workerPaths];
};

const copyDevIndexes = async ({
	buildPath,
	reactIndexesPath,
	reactPagesPath,
	svelteDir,
	svelteEntries,
	sveltePagesPath,
	vueDir,
	vueEntries,
	vuePagesPath
}: {
	buildPath: string;
	reactIndexesPath: string | false | undefined;
	reactPagesPath: string | false | undefined;
	svelteDir: string | false | undefined;
	svelteEntries: string[];
	sveltePagesPath: string | false | undefined;
	vueDir: string | false | undefined;
	vueEntries: string[];
	vuePagesPath: string | false | undefined;
}) => {
	const { readdirSync: readDir } = await import('node:fs');
	const devIndexDir = join(buildPath, '_src_indexes');
	mkdirSync(devIndexDir, { recursive: true });

	if (reactIndexesPath && reactPagesPath) {
		copyReactDevIndexes(
			reactIndexesPath,
			reactPagesPath,
			devIndexDir,
			readDir
		);
	}

	if (svelteDir && sveltePagesPath) {
		copySvelteDevIndexes(
			svelteDir,
			sveltePagesPath,
			svelteEntries,
			devIndexDir
		);
	}

	if (vueDir && vuePagesPath) {
		copyVueDevIndexes(vueDir, vuePagesPath, vueEntries, devIndexDir);
	}
};

const copyReactDevIndexes = (
	reactIndexesPath: string,
	reactPagesPath: string,
	devIndexDir: string,
	readDir: (path: string) => string[]
) => {
	if (!existsSync(reactIndexesPath)) {
		return;
	}

	const indexFiles = readDir(reactIndexesPath).filter((file: string) =>
		file.endsWith('.tsx')
	);
	const pagesRel = relative(process.cwd(), resolve(reactPagesPath)).replace(
		/\\/g,
		'/'
	);

	for (const file of indexFiles) {
		let content = readFileSync(join(reactIndexesPath, file), 'utf-8');
		content = content.replace(
			/from\s*['"]([^'"]*\/pages\/([^'"]+))['"]/g,
			(_match, _fullPath, componentName) =>
				`from '/@src/${pagesRel}/${componentName}'`
		);
		writeFileSync(join(devIndexDir, file), content);
	}
};

const copySvelteDevIndexes = (
	svelteDir: string,
	sveltePagesPath: string,
	svelteEntries: string[],
	devIndexDir: string
) => {
	const svelteIndexDir = join(getFrameworkGeneratedDir('svelte'), 'indexes');
	const sveltePageEntries = svelteEntries.filter((file) =>
		resolve(file).startsWith(resolve(sveltePagesPath))
	);
	for (const entry of sveltePageEntries) {
		const name = basename(entry).replace(/\.svelte(\.(ts|js))?$/, '');
		const indexFile = join(svelteIndexDir, 'pages', `${name}.js`);
		if (!existsSync(indexFile)) continue;
		let content = readFileSync(indexFile, 'utf-8');
		const srcRel = relative(process.cwd(), resolve(entry)).replace(
			/\\/g,
			'/'
		);
		content = content.replace(
			/import\s+Component\s+from\s+['"]([^'"]+)['"]/,
			`import Component from "/@src/${srcRel}"`
		);
		writeFileSync(join(devIndexDir, `${name}.svelte.js`), content);
	}
};

const copyVueDevIndexes = (
	vueDir: string,
	vuePagesPath: string,
	vueEntries: string[],
	devIndexDir: string
) => {
	const vueIndexDir = join(getFrameworkGeneratedDir('vue'), 'indexes');
	const vuePageEntries = vueEntries.filter((file) =>
		resolve(file).startsWith(resolve(vuePagesPath))
	);
	for (const entry of vuePageEntries) {
		const name = basename(entry, '.vue');
		const indexFile = join(vueIndexDir, `${name}.js`);
		if (!existsSync(indexFile)) continue;
		let content = readFileSync(indexFile, 'utf-8');
		const srcRel = relative(process.cwd(), resolve(entry)).replace(
			/\\/g,
			'/'
		);
		content = content.replace(
			/import\s+Comp(?:\s*,\s*\*\s+as\s+\w+)?\s+from\s+['"]([^'"]+)['"]/,
			(match) =>
				match.replace(/from\s+['"][^'"]+['"]/, `from "/@src/${srcRel}"`)
		);
		writeFileSync(join(devIndexDir, `${name}.vue.js`), content);
	}
};

const resolveVueRuntimeId = (
	content: string,
	firstUseName: string,
	outputPath: string,
	projectRoot: string
) => {
	const varIdx = content.indexOf(`var ${firstUseName} =`);
	if (varIdx <= 0) return JSON.stringify(outputPath);
	// Find all // src/...js comments before the var declaration
	const before = content.slice(0, varIdx);
	const allComments = [...before.matchAll(/\/\/\s*(src\/[^\n]*\.js)\s*\n/g)];
	// Use the last one (closest to the var declaration)
	const last = allComments[allComments.length - 1];
	if (!last?.[1]) return JSON.stringify(outputPath);
	const srcPath = resolve(
		projectRoot,
		last[1].replace('/client/', '/').replace(/\.js$/, '.ts')
	);

	return JSON.stringify(srcPath);
};

const QUOTE_CHARS = new Set(['"', "'", '`']);
const OPEN_BRACES = new Set(['{', '(']);
const CLOSE_BRACES = new Set(['}', ')']);

const findFunctionExpressionEnd = (content: string, startPos: number) => {
	let depth = 0;
	let inStr: string | false = false;
	for (let i = startPos; i < content.length; i++) {
		const char = content[i] ?? '';
		if (inStr && char === inStr && content[i - 1] !== '\\') inStr = false;
		if (inStr) continue;
		if (QUOTE_CHARS.has(char)) inStr = char;
		if (QUOTE_CHARS.has(char)) continue;
		if (OPEN_BRACES.has(char)) depth++;
		if (CLOSE_BRACES.has(char)) depth--;
		if (depth === 0 && char === ';') return i;
	}

	return startPos;
};

const wrapUseFunctions = (content: string, useNames: string[]) => {
	let result = content;
	for (const name of useNames) {
		const marker = `var ${name} = `;
		const pos = result.indexOf(marker);
		if (pos === UNFOUND_INDEX) continue;
		const afterMarker = pos + marker.length;
		const endPos = findFunctionExpressionEnd(result, afterMarker);
		const funcBody = result.slice(afterMarker, endPos);
		result = `${result.slice(0, afterMarker)}__hmr_wrap(${JSON.stringify(name)}, ${funcBody})${result.slice(endPos)}`;
	}

	return result;
};

const VUE_HMR_RUNTIME = [
	`var __hmr_cs=(globalThis.__HMR_COMPOSABLE_STATE__??={});`,
	`var __hmr_mid=__HMR_MID__;`,
	`var __hmr_prev_refs=__hmr_cs[__hmr_mid];`,
	`var __hmr_idx={};`,
	`__hmr_cs[__hmr_mid]={};`,
	`function __hmr_wrap(n,fn){return function(){`,
	`var i=(__hmr_idx[n]=(__hmr_idx[n]??-1)+1);`,
	`var r=fn.apply(this,arguments);`,
	`if(r&&typeof r==="object"){`,
	`var refs={};for(var k in r){var v=r[k];`,
	`if(v&&typeof v==="object"&&"value"in v&&!v.effect&&typeof v.value!=="function")refs[k]=v;}`,
	`(__hmr_cs[__hmr_mid][n]??=[])[i]=refs;`,
	`if(__hmr_prev_refs&&__hmr_prev_refs[n]&&__hmr_prev_refs[n][i]){`,
	`var old=__hmr_prev_refs[n][i];`,
	`for(var k in old){var nv=r[k],ov=old[k];`,
	`if(nv&&ov&&typeof nv==="object"&&"value"in nv&&!nv.effect&&typeof nv.value===typeof ov.value)nv.value=ov.value;}`,
	`}}return r;};}`
].join('');

const injectVueComposableTracking = (
	outputPath: string,
	projectRoot: string
) => {
	let content = readFileSync(outputPath, 'utf-8');
	// Find `var useXxx = (` patterns and the source file comment above them
	const usePattern = /^var\s+(use[A-Z]\w*)\s*=/gm;
	const useNames: string[] = [];
	let match;
	while ((match = usePattern.exec(content)) !== null) {
		if (match[1]) useNames.push(match[1]);
	}
	if (useNames.length === 0) return;

	const [firstUseName] = useNames;
	if (!firstUseName) return;

	const runtimeId = resolveVueRuntimeId(
		content,
		firstUseName,
		outputPath,
		projectRoot
	);
	const runtime = VUE_HMR_RUNTIME.replace('__HMR_MID__', runtimeId);

	// Insert runtime before the first use* function
	const firstUseIdx = content.indexOf(`var ${firstUseName} =`);
	if (firstUseIdx === UNFOUND_INDEX) return;
	content = `${content.slice(0, firstUseIdx) + runtime}\n${content.slice(firstUseIdx)}`;

	content = wrapUseFunctions(content, useNames);
	writeFileSync(outputPath, content);
};

const buildDevUrlFileMap = (
	urlReferencedFiles: string[],
	projectRoot: string
) => {
	const urlFileMap = new Map<string, string>();
	for (const srcPath of urlReferencedFiles) {
		const rel = relative(projectRoot, srcPath).replace(/\\/g, '/');
		const name = basename(srcPath);
		const mtime = Math.round(statSync(srcPath).mtimeMs);
		const url = `/@src/${rel}?v=${mtime}`;
		urlFileMap.set(name, url);
		// Also map .js variant for when Bun rewrites .ts → .js
		urlFileMap.set(name.replace(/\.tsx?$/, '.js'), url);
	}

	return urlFileMap;
};

const buildProdUrlFileMap = (
	urlReferencedFiles: string[],
	buildPath: string,
	nonReactClientOutputs: BuildArtifact[]
) => {
	const urlFileMap = new Map<string, string>();
	for (const srcPath of urlReferencedFiles) {
		const srcBase = basename(srcPath).replace(/\.[^.]+$/, '');
		const output = nonReactClientOutputs.find((artifact) =>
			basename(artifact.path).startsWith(`${srcBase}.`)
		);
		if (!output) continue;
		urlFileMap.set(
			basename(srcPath),
			`/${relative(buildPath, output.path).replace(/\\/g, '/')}`
		);
	}

	return urlFileMap;
};

const buildUrlFileMap = (
	urlReferencedFiles: string[],
	hmr: boolean,
	projectRoot: string,
	buildPath: string,
	nonReactClientOutputs: BuildArtifact[]
) => {
	if (hmr) return buildDevUrlFileMap(urlReferencedFiles, projectRoot);

	return buildProdUrlFileMap(
		urlReferencedFiles,
		buildPath,
		nonReactClientOutputs
	);
};

const rewriteUrlReferences = (
	outputPaths: string[],
	urlFileMap: Map<string, string>
) => {
	const urlPattern =
		/new\s+URL\(\s*["'](\.\.?\/[^"']+)["']\s*,\s*import\.meta\.url\s*\)/g;
	for (const outputPath of outputPaths) {
		let content = readFileSync(outputPath, 'utf-8');
		let changed = false;
		content = content.replace(urlPattern, (_match, relPath) => {
			const targetName = basename(relPath);
			const resolvedPath = urlFileMap.get(targetName);
			if (!resolvedPath) return _match;
			changed = true;

			return `new URL('${resolvedPath}', import.meta.url)`;
		});
		if (changed) writeFileSync(outputPath, content);
	}
};

const vueFeatureFlags: Record<string, string> = {
	__VUE_OPTIONS_API__: 'true',
	__VUE_PROD_DEVTOOLS__: isDev ? 'true' : 'false',
	__VUE_PROD_HYDRATION_MISMATCH_DETAILS__: isDev ? 'true' : 'false'
};

const bunBuildPassKeys: BunBuildPassKey[] = [
	'server',
	'reactClient',
	'nonReactClient',
	'islandClient',
	'globalCss',
	'vueCss'
];
const bunBuildPassKeySet = new Set<string>(['default', ...bunBuildPassKeys]);
const reservedBunBuildConfigKeys = new Set<string>([
	'entrypoints',
	'outdir',
	'outfile',
	'root',
	'target',
	'format',
	'throw',
	'compile'
]);

/** Keys whose values are structurally required per pass and must not be
 *  overridable by user `bunBuildConfig`. `splitting` on the React-client
 *  pass is the canonical example: turning it off would inline React into
 *  every page bundle and break the React vendor pipeline that other code
 *  paths depend on. (Vendor passes — `buildAngularVendor`, `buildReactVendor`,
 *  etc. — don't go through `mergeBunBuildConfig` at all, so they're inherently
 *  locked.) */
const passLockedKeys: Partial<Record<BunBuildPassKey, Set<string>>> = {
	reactClient: new Set(['splitting'])
};

type BunBuildOptions = Parameters<typeof bunBuild>[0];

const isObject = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null;

const isBunBuildPassConfig = (
	config: BuildConfig['bunBuild']
): config is BunBuildPassConfig =>
	isObject(config) &&
	Object.keys(config).some((key) => bunBuildPassKeySet.has(key));

const sanitizeBunBuildOverride = (
	override: BunBuildConfigOverride | undefined,
	extraReservedKeys: Set<string> = new Set()
) => {
	if (!override) return {};
	const sanitized: Record<string, unknown> = { ...override };
	for (const key of reservedBunBuildConfigKeys) {
		delete sanitized[key];
	}
	for (const key of extraReservedKeys) {
		delete sanitized[key];
	}

	return sanitized as BunBuildConfigOverride;
};

export const resolveBunBuildOverride = (
	config: BuildConfig['bunBuild'],
	pass: BunBuildPassKey
) => {
	const locked = passLockedKeys[pass] ?? new Set<string>();
	if (!config) return {};
	if (!isBunBuildPassConfig(config)) {
		return sanitizeBunBuildOverride(config, locked);
	}

	return sanitizeBunBuildOverride(
		{
			...(config.default ?? {}),
			...(config[pass] ?? {})
		},
		locked
	);
};

/** Resolve the Bun `sourcemap` mode for CLIENT bundles from the `sourcemaps`
 *  config. Dev always uses inline maps (HMR / devtools); production emits
 *  external maps only when explicitly enabled — they're then chained back to
 *  source + moved to a private dir by `privatizeClientSourcemaps`. */
export const resolveClientSourcemap = (
	sourcemaps: BuildConfig['sourcemaps'],
	isDev: boolean
): 'inline' | 'external' | 'none' => {
	if (isDev) return sourcemaps === false ? 'none' : 'inline';
	if (sourcemaps === 'external' || sourcemaps === true) return 'external';
	if (sourcemaps === 'inline') return 'inline';

	return 'none';
};

const dedupe = <T>(values: T[]) => [...new Set(values)];

export const mergeBunBuildConfig = (
	base: BunBuildOptions,
	override: BunBuildConfigOverride
) => {
	const sanitized = sanitizeBunBuildOverride(override);
	const merged = {
		...base,
		...sanitized
	} as BunBuildOptions;

	return {
		...merged,
		define:
			base.define || sanitized.define
				? {
						...(sanitized.define ?? {}),
						...(base.define ?? {})
					}
				: undefined,
		external: dedupe([
			...(base.external ?? []),
			...(sanitized.external ?? [])
		]),
		plugins: [...(base.plugins ?? []), ...(sanitized.plugins ?? [])]
	} as BunBuildOptions;
};

const buildUnlocked = async ({
	buildDirectory = 'build',
	assetsDirectory,
	publicDirectory,
	islands,
	reactDirectory,
	htmlDirectory,
	htmxDirectory,
	angularDirectory,
	emberDirectory,
	svelteDirectory,
	vueDirectory,
	stylesConfig,
	stylePreprocessors,
	postcss,
	tailwind,
	bunBuild: bunBuildConfig,
	options,
	incrementalFiles,
	mode,
	sitemap,
	sourcemaps
}: BuildConfig) => {
	const buildStart = performance.now();
	const projectRoot = cwd();
	const traceEnabled = isBuildTraceEnabled();
	const traceEvents: BuildTraceEvent[] = [];
	let traceFrameworkNames: string[] = [];
	const traceGlobal = globalThis as typeof globalThis & {
		__absoluteBuildTracePhase?: BuildTracePhase;
	};
	const previousTracePhase = traceGlobal.__absoluteBuildTracePhase;
	const restoreTracePhase = () => {
		if (previousTracePhase) {
			traceGlobal.__absoluteBuildTracePhase = previousTracePhase;
		} else {
			delete traceGlobal.__absoluteBuildTracePhase;
		}
	};
	const tracePhase: BuildTracePhase = async <T>(
		name: string,
		fn: () => Promise<T> | T,
		metadata?: Record<string, unknown>
	) => {
		if (!traceEnabled) return fn();
		const phaseStart = performance.now();
		try {
			const result = await fn();
			traceEvents.push({
				durationMs: performance.now() - phaseStart,
				metadata,
				name,
				ok: true,
				startMs: phaseStart - buildStart
			});

			return result;
		} catch (error) {
			traceEvents.push({
				durationMs: performance.now() - phaseStart,
				metadata: {
					...metadata,
					error:
						error instanceof Error ? error.message : String(error)
				},
				name,
				ok: false,
				startMs: phaseStart - buildStart
			});
			throw error;
		}
	};
	if (traceEnabled) {
		traceGlobal.__absoluteBuildTracePhase = tracePhase;
	}
	const writeBuildTrace = (buildPath: string) => {
		if (!traceEnabled) {
			restoreTracePhase();

			return;
		}
		const traceDir = join(buildPath, '.absolute-trace');
		const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
		mkdirSync(traceDir, { recursive: true });
		writeFileSync(
			join(traceDir, `build-trace-${timestamp}.json`),
			JSON.stringify(
				{
					events: traceEvents,
					frameworks: traceFrameworkNames,
					generatedAt: new Date().toISOString(),
					mode: mode ?? (isDev ? 'development' : 'production'),
					totalDurationMs: performance.now() - buildStart,
					version: 1
				},
				null,
				2
			)
		);
		restoreTracePhase();
	};

	await tracePhase('absolute/version', () => resolveAbsoluteVersion());
	const isIncremental = incrementalFiles && incrementalFiles.length > 0;
	const styleTransformConfig = createStyleTransformConfig(
		stylePreprocessors,
		postcss
	);
	const stylePreprocessorPlugin =
		createStylePreprocessorPlugin(styleTransformConfig);

	// Normalize incrementalFiles for consistent cross-platform path checking
	const normalizedIncrementalFiles = incrementalFiles?.map(normalizePath);

	const throwOnError = options?.throwOnError === true;
	const hmr = options?.injectHMR === true;
	const buildPath = validateSafePath(buildDirectory, projectRoot);
	const assetsPath =
		assetsDirectory && validateSafePath(assetsDirectory, projectRoot);
	const reactDir =
		reactDirectory && validateSafePath(reactDirectory, projectRoot);
	const htmlDir =
		htmlDirectory && validateSafePath(htmlDirectory, projectRoot);
	const htmxDir =
		htmxDirectory && validateSafePath(htmxDirectory, projectRoot);
	const svelteDir =
		svelteDirectory && validateSafePath(svelteDirectory, projectRoot);
	const vueDir = vueDirectory && validateSafePath(vueDirectory, projectRoot);
	const angularDir =
		angularDirectory && validateSafePath(angularDirectory, projectRoot);
	const emberDir =
		emberDirectory && validateSafePath(emberDirectory, projectRoot);
	const islandBootstrapPath =
		islands?.bootstrap && validateSafePath(islands.bootstrap, projectRoot);
	const islandRegistryPath =
		islands?.registry && validateSafePath(islands.registry, projectRoot);
	const stylesPath =
		typeof stylesConfig === 'string' ? stylesConfig : stylesConfig?.path;
	const stylesIgnore =
		typeof stylesConfig === 'object' ? stylesConfig.ignore : undefined;
	const stylesDir = stylesPath && validateSafePath(stylesPath, projectRoot);

	const reactIndexesPath =
		reactDir && join(getFrameworkGeneratedDir('react'), 'indexes');
	const reactPagesPath = reactDir && join(reactDir, 'pages');
	const htmlPagesPath = htmlDir && join(htmlDir, 'pages');
	const htmlScriptsPath = htmlDir && join(htmlDir, 'scripts');
	const sveltePagesPath = svelteDir && join(svelteDir, 'pages');
	const vuePagesPath = vueDir && join(vueDir, 'pages');
	const htmxPagesPath = htmxDir && join(htmxDir, 'pages');
	const htmxScriptsPath = htmxDir && join(htmxDir, 'scripts');
	const angularPagesPath = angularDir && join(angularDir, 'pages');
	const emberPagesPath = emberDir && join(emberDir, 'pages');

	const frontends = [
		reactDir,
		htmlDir,
		htmxDir,
		svelteDir,
		vueDir,
		angularDir,
		emberDir
	].filter(Boolean);
	const isSingle = frontends.length === 1;

	const frameworkNames = [
		reactDir && 'react',
		htmlDir && 'html',
		htmxDir && 'htmx',
		svelteDir && 'svelte',
		vueDir && 'vue',
		angularDir && 'angular',
		emberDir && 'ember'
	].filter((name): name is string => Boolean(name));
	traceFrameworkNames = frameworkNames;
	sendTelemetryEvent('build:start', {
		framework: frameworkNames[0],
		frameworks: frameworkNames,
		mode: mode ?? (isDev ? 'development' : 'production'),
		tailwind: Boolean(tailwind)
	});

	// Generated intermediate files now live under
	// `<projectRoot>/.absolutejs/generated/<framework>/`, so the Bun.build
	// `root` for client bundling has to be the parent of those framework
	// dirs — the project's `.absolutejs/generated/` cache root. Strip math:
	//   entry: <projectRoot>/.absolutejs/generated/angular/pages/x.js
	//   root:  <projectRoot>/.absolutejs/generated
	//   → output: <buildPath>/angular/pages/x.<hash>.js
	// HTML/HTMX entries don't go through framework compilers; we keep their
	// source dir in the candidate list and fall back to a common ancestor
	// when both kinds are present.
	const generatedRoot = getGeneratedRoot(projectRoot);
	const sourceClientRoots = [
		htmlDir,
		htmxDir,
		islandBootstrapPath && dirname(islandBootstrapPath)
	].filter((dir): dir is string => Boolean(dir));
	const usesGenerated =
		Boolean(reactDir) ||
		Boolean(svelteDir) ||
		Boolean(vueDir) ||
		Boolean(angularDir);
	if (usesGenerated) sourceClientRoots.push(generatedRoot);
	const clientRoot =
		sourceClientRoots.length === 1
			? (sourceClientRoots[0] ?? projectRoot)
			: commonAncestor(sourceClientRoots, projectRoot);

	const serverDirMap: { dir: string; subdir: string }[] = [];
	if (svelteDir)
		serverDirMap.push({
			dir: getFrameworkGeneratedDir('svelte', projectRoot),
			subdir: 'server'
		});
	if (vueDir)
		serverDirMap.push({
			dir: getFrameworkGeneratedDir('vue', projectRoot),
			subdir: 'server'
		});
	if (angularDir)
		serverDirMap.push({
			dir: getFrameworkGeneratedDir('angular', projectRoot),
			subdir: ''
		});

	let serverOutDir: string | undefined;
	let serverRoot: string | undefined;

	if (serverDirMap.length === 1) {
		const [firstEntry] = serverDirMap;
		if (!firstEntry)
			throw new Error('Expected at least one server directory entry');
		serverRoot = join(firstEntry.dir, firstEntry.subdir);
		serverOutDir = join(buildPath, basename(firstEntry.dir));
	} else if (serverDirMap.length > 1) {
		// Use framework dirs (not generated subdirs) as input to commonAncestor
		// so the root directory actually exists on disk.
		serverRoot = commonAncestor(
			serverDirMap.map((entry) => entry.dir),
			projectRoot
		);
		serverOutDir = buildPath;
	}

	const publicPath =
		publicDirectory && validateSafePath(publicDirectory, projectRoot);
	await tracePhase('build-dir/create', () =>
		mkdirSync(buildPath, { recursive: true })
	);

	if (publicPath)
		await tracePhase('public/copy', () =>
			cpSync(publicPath, buildPath, { force: true, recursive: true })
		);

	// Helper to find matching entry points for incremental files
	// The dependency graph already includes all dependent files in incrementalFiles
	const filterToIncrementalEntries = (
		entryPoints: string[],
		mapToSource: (entry: string) => string | null
	) => {
		if (!isIncremental || !incrementalFiles) return entryPoints;

		const normalizedIncremental = new Set(
			incrementalFiles.map((f) => resolve(f))
		);
		const matchingEntries: string[] = [];

		for (const entry of entryPoints) {
			const sourceFile = mapToSource(entry);
			if (!sourceFile) continue;
			if (!normalizedIncremental.has(resolve(sourceFile))) continue;
			matchingEntries.push(entry);
		}

		return matchingEntries;
	};

	// For incremental React builds, only generate indexes for changed files
	// NOTE: We always regenerate index files to ensure they have the latest hydration error handling logic
	if (reactIndexesPath && reactPagesPath) {
		// Always regenerate React index files to ensure latest error handling is included
		// This is safe because index files are small and generation is fast
		await tracePhase('react/index-generation', () =>
			generateReactIndexFiles(reactPagesPath, reactIndexesPath, hmr)
		);
	}

	// Copy assets on full builds or if assets changed
	if (
		assetsPath &&
		(!isIncremental ||
			normalizedIncrementalFiles?.some((f) => f.includes('/assets/')))
	) {
		await tracePhase('assets/copy', () =>
			cpSync(assetsPath, join(buildPath, 'assets'), {
				force: true,
				recursive: true
			})
		);
	}

	// Tailwind + entry point scanning run in parallel (they're independent).
	// Tailwind also has to rerun whenever any file it scans for utility-class
	// candidates changes — that includes JS/TS/JSX/TSX/Vue/Svelte/HTML and the
	// other source extensions referenced via `@source`, not only stylesheets.
	// Without this, classes added to markup never show up in the emitted CSS.
	const tailwindPromise =
		tailwind &&
		(!isIncremental ||
			normalizedIncrementalFiles?.some(isTailwindCandidate))
			? tracePhase('tailwind/build', () =>
					compileTailwindConfig(
						tailwind,
						buildPath,
						styleTransformConfig,
						computeFrameworkTailwindSources({
							angularDirectory,
							emberDirectory,
							htmlDirectory,
							htmxDirectory,
							reactDirectory,
							svelteDirectory,
							vueDirectory
						} as BuildConfig),
						normalizedIncrementalFiles ?? []
					)
				)
			: undefined;

	const emptyConventionResult: {
		conventions: undefined;
		pageFiles: string[];
	} = {
		conventions: undefined,
		pageFiles: []
	};
	// Universal HTML conventions can live in any framework's pages dir —
	// `error.html` / `not-found.html` are framework-agnostic and aggregate
	// into a single conventionsMap.html bucket.
	const htmlConventionDirs = [
		reactPagesPath,
		sveltePagesPath,
		vuePagesPath,
		angularPagesPath,
		emberPagesPath,
		htmlPagesPath,
		htmxPagesPath
	].filter((path): path is string => Boolean(path));
	const [
		,
		allReactEntries,
		allHtmlEntries,
		reactConventionResult,
		svelteConventionResult,
		vueConventionResult,
		angularConventionResult,
		emberConventionResult,
		htmlConventionResults,
		allGlobalCssEntries
	] = await Promise.all([
		tailwindPromise,
		reactIndexesPath
			? tracePhase('scan/react-indexes', () =>
					scanEntryPoints(reactIndexesPath, '*.tsx')
				)
			: [],
		tracePhase('scan/html-htmx-scripts', async () => {
			const entries = await Promise.all(
				[htmlScriptsPath, htmxScriptsPath]
					.filter((path): path is string => Boolean(path))
					.map((path) => scanEntryPoints(path, '*.{js,ts}'))
			);

			return entries.flat();
		}),
		reactPagesPath
			? tracePhase('scan/react-conventions', () =>
					scanConventions(reactPagesPath, '*.tsx')
				)
			: emptyConventionResult,
		sveltePagesPath
			? tracePhase('scan/svelte-conventions', () =>
					scanConventions(sveltePagesPath, '*.svelte')
				)
			: emptyConventionResult,
		vuePagesPath
			? tracePhase('scan/vue-conventions', () =>
					scanConventions(vuePagesPath, '*.vue')
				)
			: emptyConventionResult,
		angularPagesPath
			? tracePhase('scan/angular-conventions', () =>
					scanConventions(angularPagesPath, '**/*.ts')
				)
			: emptyConventionResult,
		emberPagesPath
			? tracePhase('scan/ember-conventions', () =>
					scanConventions(emberPagesPath, '*.{gjs,gts,ts}')
				)
			: emptyConventionResult,
		tracePhase('scan/html-conventions', async () =>
			Promise.all(
				htmlConventionDirs.map((dir) => scanConventions(dir, '*.html'))
			)
		),
		stylesDir
			? tracePhase('scan/css', () =>
					scanCssEntryPoints(stylesDir, stylesIgnore)
				)
			: []
	]);

	// Convention files (colocated with pages) for error/loading/not-found
	const allSvelteEntries = svelteConventionResult.pageFiles;
	const allVueEntries = vueConventionResult.pageFiles;
	const allAngularEntries = angularConventionResult.pageFiles;
	const allEmberEntries = emberConventionResult.pageFiles;

	const conventionsMap: ConventionsMap = {};
	if (reactConventionResult.conventions)
		conventionsMap.react = reactConventionResult.conventions;
	if (svelteConventionResult.conventions)
		conventionsMap.svelte = svelteConventionResult.conventions;
	if (vueConventionResult.conventions)
		conventionsMap.vue = vueConventionResult.conventions;
	if (angularConventionResult.conventions)
		conventionsMap.angular = angularConventionResult.conventions;
	if (emberConventionResult.conventions)
		conventionsMap.ember = emberConventionResult.conventions;
	// Merge html convention scans from every framework pages dir. error.html
	// / not-found.html in any pages dir become the universal HTML fallback;
	// later directories lose to earlier ones on collision (warn).
	const htmlDefaults: FrameworkConventions = {};
	const htmlPages: Record<string, PageConventions> = {};
	const htmlConventionSources: string[] = [];
	for (let idx = 0; idx < htmlConventionResults.length; idx++) {
		const result = htmlConventionResults[idx];
		if (!result?.conventions) continue;

		const dirLabel = htmlConventionDirs[idx] ?? '<unknown>';
		const { defaults: scannedDefaults, pages: scannedPages } =
			result.conventions;
		if (scannedDefaults?.error) {
			if (htmlDefaults.error) {
				logWarn(
					`Multiple error.html files found; using ${htmlDefaults.error} and ignoring ${scannedDefaults.error} (${dirLabel}).`
				);
			} else {
				htmlDefaults.error = scannedDefaults.error;
				htmlConventionSources.push(scannedDefaults.error);
			}
		}
		if (scannedDefaults?.notFound) {
			if (htmlDefaults.notFound) {
				logWarn(
					`Multiple not-found.html files found; using ${htmlDefaults.notFound} and ignoring ${scannedDefaults.notFound} (${dirLabel}).`
				);
			} else {
				htmlDefaults.notFound = scannedDefaults.notFound;
				htmlConventionSources.push(scannedDefaults.notFound);
			}
		}
		if (scannedDefaults?.loading && !htmlDefaults.loading) {
			htmlDefaults.loading = scannedDefaults.loading;
			htmlConventionSources.push(scannedDefaults.loading);
		}
		if (scannedPages) {
			for (const [pageName, page] of Object.entries(scannedPages)) {
				if (!htmlPages[pageName]) htmlPages[pageName] = {};
				if (page.error && !htmlPages[pageName].error) {
					htmlPages[pageName].error = page.error;
					htmlConventionSources.push(page.error);
				}
				if (page.loading && !htmlPages[pageName].loading) {
					htmlPages[pageName].loading = page.loading;
					htmlConventionSources.push(page.loading);
				}
			}
		}
	}
	if (
		htmlDefaults.error ||
		htmlDefaults.notFound ||
		htmlDefaults.loading ||
		Object.keys(htmlPages).length > 0
	) {
		// Materialize html convention files into a stable build/conventions/html/
		// location so production runtime can read them after the source tree is
		// stripped. cpSync keeps the file as-is (no compile step needed for html).
		const htmlConventionsOutDir = join(buildPath, 'conventions', 'html');
		mkdirSync(htmlConventionsOutDir, { recursive: true });
		const htmlPathRemap = new Map<string, string>();
		for (const sourcePath of htmlConventionSources) {
			const dest = join(htmlConventionsOutDir, basename(sourcePath));
			cpSync(sourcePath, dest, { force: true });
			htmlPathRemap.set(sourcePath, dest);
		}
		const remap = (path: string | undefined) =>
			path ? (htmlPathRemap.get(path) ?? path) : undefined;

		const htmlConventions: FrameworkConventionEntry = {};
		const remappedDefaults: FrameworkConventions = {};
		const errorRemap = remap(htmlDefaults.error);
		if (errorRemap) remappedDefaults.error = errorRemap;
		const notFoundRemap = remap(htmlDefaults.notFound);
		if (notFoundRemap) remappedDefaults.notFound = notFoundRemap;
		const loadingRemap = remap(htmlDefaults.loading);
		if (loadingRemap) remappedDefaults.loading = loadingRemap;
		if (
			remappedDefaults.error ||
			remappedDefaults.notFound ||
			remappedDefaults.loading
		) {
			htmlConventions.defaults = remappedDefaults;
		}
		if (Object.keys(htmlPages).length > 0) {
			const remappedPages: Record<string, PageConventions> = {};
			for (const [pageName, page] of Object.entries(htmlPages)) {
				const entry: PageConventions = {};
				const errorPath = remap(page.error);
				if (errorPath) entry.error = errorPath;
				const loadingPath = remap(page.loading);
				if (loadingPath) entry.loading = loadingPath;
				remappedPages[pageName] = entry;
			}
			htmlConventions.pages = remappedPages;
		}
		conventionsMap.html = htmlConventions;
	}

	// Warn if multiple frameworks define not-found convention files
	const notFoundFrameworks = (
		['react', 'svelte', 'vue', 'angular', 'ember', 'html'] as const
	).filter((framework) => conventionsMap[framework]?.defaults?.notFound);
	if (notFoundFrameworks.length > 1) {
		logWarn(
			`Multiple frameworks define not-found convention files: ${notFoundFrameworks.join(', ')}. ` +
				`Only one will be used (priority: ${notFoundFrameworks[0]}). ` +
				`Remove not-found files from other frameworks to avoid ambiguity.`
		);
	}
	// When HTML/HTMX pages change, we must include their CSS and scripts in the build
	// so the manifest has those entries for updateAssetPaths. Otherwise incremental
	// builds drop them and updateAssetPaths fails with "no manifest entry".
	const shouldIncludeHtmlAssets =
		!isIncremental ||
		normalizedIncrementalFiles?.some(
			(f) =>
				f.includes('/html/') && (f.endsWith('.html') || isStylePath(f))
		);
	// Filter entries for incremental builds
	// For React: map index entries back to their source pages
	const reactEntries =
		isIncremental && reactIndexesPath && reactPagesPath
			? filterToIncrementalEntries(allReactEntries, (entry) => {
					// Map index entry (indexes/ReactExample.tsx) to source page (pages/ReactExample.tsx)
					if (entry.startsWith(resolve(reactIndexesPath))) {
						const pageName = basename(entry, '.tsx');

						return join(reactPagesPath, `${pageName}.tsx`);
					}

					return null;
				})
			: allReactEntries;

	const htmlEntries =
		isIncremental &&
		(htmlScriptsPath || htmxScriptsPath) &&
		!shouldIncludeHtmlAssets
			? filterToIncrementalEntries(allHtmlEntries, (entry) => entry)
			: allHtmlEntries;

	// For Svelte/Vue/Angular: entries are the page files themselves
	const svelteEntries = isIncremental
		? filterToIncrementalEntries(allSvelteEntries, (entry) => entry)
		: allSvelteEntries;

	const vueEntries = isIncremental
		? filterToIncrementalEntries(allVueEntries, (entry) => entry)
		: allVueEntries;

	const angularEntries = isIncremental
		? filterToIncrementalEntries(allAngularEntries, (entry) => entry)
		: allAngularEntries;

	const emberEntries = isIncremental
		? filterToIncrementalEntries(allEmberEntries, (entry) => entry)
		: allEmberEntries;

	// CSS entries - entries are the style files themselves
	const globalCssEntries = isIncremental
		? filterToIncrementalEntries(allGlobalCssEntries, (entry) => entry)
		: allGlobalCssEntries;

	// Start HMR client build early — it has no dependency on compile/bunBuild
	// results and will resolve during the compile phase for free.
	const hmrClientBundlePromise =
		hmr && (htmlDir || htmxDir) ? buildHMRClient() : undefined;
	const allFrameworkDirs = [
		reactDir,
		svelteDir,
		vueDir,
		angularDir,
		htmlDir,
		htmxDir
	].filter((dir): dir is string => Boolean(dir));
	const urlReferencedFilesPromise = tracePhase('scan/worker-references', () =>
		scanWorkerReferences(allFrameworkDirs)
	);

	// Angular HMR Optimization — Skip Svelte/Vue compilation when their entries are
	// empty during incremental builds (avoids importing/initializing unused compilers)
	const shouldCompileSvelte = svelteDir && svelteEntries.length > 0;
	const shouldCompileVue = vueDir && vueEntries.length > 0;
	const shouldCompileAngular = angularDir && angularEntries.length > 0;

	// Resolve the SSR-only Vue page set by statically scanning project
	// source for `handleVuePageRequest({ client: 'none', ... })` calls,
	// then mapping the literal manifest keys back to absolute .vue entry
	// paths. SSR-only pages skip the client hydration index and the
	// per-page client bundle — see `scanVueSsrOnlyPages` for the
	// detection contract. Falls back to empty (i.e. every Vue page gets
	// a client bundle, today's behavior) whenever the static scan can't
	// resolve a name; never produces false positives.
	const ssrOnlyVueEntries = shouldCompileVue
		? await tracePhase('scan/vue-ssr-only', async () => {
				const { scanVueSsrOnlyPages } = await import(
					'../build/scanVueSsrOnlyPages'
				);
				const ssrOnlyPageNames = scanVueSsrOnlyPages(projectRoot);
				if (ssrOnlyPageNames.size === 0) return new Set<string>();
				const resolved = new Set<string>();
				for (const entry of vueEntries) {
					const name = basename(entry, '.vue');
					if (ssrOnlyPageNames.has(name)) {
						resolved.add(resolve(entry));
					}
				}

				return resolved;
			})
		: new Set<string>();
	const shouldCompileEmber = emberDir && emberEntries.length > 0;

	const emptyStringArray: string[] = [];
	const islandBuildInfo = islandRegistryPath
		? await tracePhase('islands/registry', () =>
				loadIslandRegistryBuildInfo(islandRegistryPath)
			)
		: null;
	// Rewrites the island registry's eager cross-framework component imports to
	// lazy `{ source, export }` definitions in every bundle, so a host of one
	// framework doesn't try to bundle another framework's raw island source
	// (which it can't resolve/compile). See islandRegistryTransform.ts.
	const islandRegistryPlugin = islandBuildInfo
		? createIslandRegistryDefinitionPlugin({
				definitions: islandBuildInfo.definitions,
				resolvedRegistryPath: islandBuildInfo.resolvedRegistryPath
			})
		: undefined;
	const islandRegistryPlugins = islandRegistryPlugin
		? [islandRegistryPlugin]
		: [];
	const islandFrameworkSources = islandBuildInfo
		? collectIslandFrameworkSources(islandBuildInfo)
		: {};
	const islandSvelteSources =
		islandFrameworkSources.svelte ?? emptyStringArray;
	const islandVueSources = islandFrameworkSources.vue ?? emptyStringArray;
	const islandAngularSources =
		islandFrameworkSources.angular ?? emptyStringArray;
	const shouldCompileIslandSvelte =
		svelteDir && islandSvelteSources.length > 0;
	const shouldCompileIslandVue = vueDir && islandVueSources.length > 0;
	const shouldCompileIslandAngular =
		angularDir && islandAngularSources.length > 0;

	// Scan backend code for `handleAngularPageRequest({...})` calls and
	// emit per-page providers files + a shared route-mounts map under
	// `.absolutejs/generated/angular/`. Has to run BEFORE the parallel
	// compileAngular invocations below — both the page and island
	// compiles read the generated files. Cheap (AST pass over backend
	// `.ts` files, only fires when Angular is in use) and idempotent.
	let angularProvidersInjection:
		| import('../build/compileAngular').AngularProvidersInjection
		| undefined = undefined;
	let angularAppProvidersSource: string | undefined = undefined;
	if (shouldCompileAngular && angularDir) {
		await tracePhase('scan/angular-handlers', async () => {
			const { runAngularHandlerScan } = await import(
				'../build/runAngularHandlerScan'
			);
			const { parseAngularProvidersImport } = await import(
				'../build/parseAngularConfigImports'
			);
			const scanResult = runAngularHandlerScan(projectRoot, angularDir);
			const providersImport = parseAngularProvidersImport(projectRoot);
			// Build the per-page injection map whether or not a global
			// `angular.providers` binding exists. Router pages (those that
			// `export const routes`) get `provideRouter` + `APP_BASE_HREF`
			// injected from the scan alone; the global `appProviders` spread
			// is added on top only when a binding is configured.
			angularAppProvidersSource = providersImport?.absolutePath;
			const pagesByFile = new Map<
				string,
				{ hasRoutes: boolean; basePath: string | null }
			>();
			const basePathByKey = new Map<string, string | null>();
			for (const call of scanResult.calls) {
				basePathByKey.set(
					call.manifestKey,
					call.mountPath?.endsWith('/*')
						? call.mountPath.slice(0, -1)
						: null
				);
			}
			// `pageRoutes` covers every `.ts` under `angularDirectory`, so
			// this also fills entries for pages without a `routes` export
			// (they default to `hasRoutes: false`).
			for (const route of scanResult.pageRoutes) {
				const basePath = basePathByKey.get(route.manifestKey) ?? null;
				const normalizedBase = basePath === '/' ? null : basePath;
				pagesByFile.set(route.pageFile, {
					basePath: normalizedBase,
					hasRoutes: route.hasRoutes
				});
			}
			angularProvidersInjection = {
				appProvidersSource: providersImport?.absolutePath ?? null,
				pagesByFile
			};
		});
	}

	const [
		{ svelteServerPaths, svelteIndexPaths, svelteClientPaths },
		{
			vueServerPaths,
			vueIndexPaths,
			vueClientPaths,
			vueCssPaths,
			vueSpaRoutesBySource
		},
		{ clientPaths: angularClientPaths, serverPaths: angularServerPaths },
		{ clientPaths: emberClientPaths, serverPaths: emberServerPaths },
		{ svelteClientPaths: islandSvelteClientPaths },
		{ vueClientPaths: islandVueClientPaths },
		{ clientPaths: islandAngularClientPaths }
	] = await Promise.all([
		shouldCompileSvelte
			? tracePhase('compile/svelte', () =>
					import('../build/compileSvelte').then((mod) =>
						mod.compileSvelte(
							svelteEntries,
							svelteDir,
							new Map(),
							hmr,
							styleTransformConfig
						)
					)
				)
			: {
					svelteClientPaths: [...emptyStringArray],
					svelteIndexPaths: [...emptyStringArray],
					svelteServerPaths: [...emptyStringArray]
				},
		shouldCompileVue
			? tracePhase('compile/vue', () =>
					import('../build/compileVue').then((mod) =>
						mod.compileVue(
							vueEntries,
							vueDir,
							hmr,
							styleTransformConfig,
							ssrOnlyVueEntries
						)
					)
				)
			: {
					vueClientPaths: [...emptyStringArray],
					vueCssPaths: [...emptyStringArray],
					vueIndexPaths: [...emptyStringArray],
					vueServerPaths: [...emptyStringArray],
					vueSpaRoutesBySource: new Map<
						string,
						{ path: string; importPath: string }[]
					>()
				},
		shouldCompileAngular
			? tracePhase('compile/angular', async () => {
					const mod = await import('../build/compileAngular');
					// Compile the user's `angular.providers` source
					// (typically `appProviders.ts`) separately first.
					// `compileAngularFileJIT` walks its transitive `.ts`
					// imports and inlines every component's
					// `templateUrl`/`styleUrls`, writing the resulting
					// `.js` outputs into the angular generated tree.
					// `compileAngular` then references the compiled
					// `appProviders.js` directly from each page's
					// server output via an injected
					// `import { appProviders } from "<rel>"` — single
					// module graph, no `.providers.ts` indirection, no
					// circular ESM cycle when a router page exports
					// `routes`.
					if (angularAppProvidersSource && angularDir) {
						const { getFrameworkGeneratedDir } = await import(
							'../utils/generatedDir'
						);
						const compiledRoot = getFrameworkGeneratedDir(
							'angular',
							projectRoot
						);
						await mod.compileAngularFileJIT(
							angularAppProvidersSource,
							compiledRoot,
							angularDir,
							styleTransformConfig
						);
					}
					const result = await mod.compileAngular(
						angularEntries,
						angularDir,
						hmr,
						styleTransformConfig,
						angularProvidersInjection
					);
					// In dev mode, prime the fast-HMR fingerprint cache
					// for every component .ts file so the first user
					// edit has a baseline. Without this, the first
					// edit always reports `cachedFingerprint ===
					// undefined` and structural changes (`imports`,
					// `hostDirectives`, `providers`, etc.) silently
					// run through Tier 0 instead of escalating to
					// Tier 1b.
					if (hmr) {
						try {
							const { primeComponentFingerprint } = await import(
								'../dev/angular/fastHmrCompiler'
							);
							const { readdir } = await import(
								'node:fs/promises'
							);
							const { join } = await import('node:path');
							const walk = async (dir: string) => {
								const entries = await readdir(dir, {
									withFileTypes: true
								});
								const out: string[] = [];
								for (const entry of entries) {
									const full = join(dir, entry.name);
									if (entry.isDirectory()) {
										out.push(...(await walk(full)));
									} else if (
										entry.isFile() &&
										entry.name.endsWith('.ts') &&
										!entry.name.endsWith('.d.ts')
									) {
										out.push(full);
									}
								}

								return out;
							};
							const tsFiles = await walk(angularDir);
							await Promise.all(
								tsFiles.map(primeComponentFingerprint)
							);
						} catch {
							// Best-effort: if priming fails, the only
							// consequence is the pre-fix behavior
							// (first-edit-per-component falls through
							// to Tier 0).
						}
					}

					return result;
				})
			: {
					clientPaths: [...emptyStringArray],
					serverPaths: [...emptyStringArray]
				},
		shouldCompileEmber
			? tracePhase('compile/ember', () =>
					import('../build/compileEmber').then((mod) =>
						mod.compileEmber(
							emberEntries,
							emberDir,
							projectRoot,
							hmr
						)
					)
				)
			: {
					clientPaths: [...emptyStringArray],
					serverPaths: [...emptyStringArray]
				},
		shouldCompileIslandSvelte
			? tracePhase('compile/island-svelte', () =>
					import('../build/compileSvelte').then((mod) =>
						mod.compileSvelte(
							islandSvelteSources,
							svelteDir,
							new Map(),
							hmr,
							styleTransformConfig
						)
					)
				)
			: {
					svelteClientPaths: [...emptyStringArray]
				},
		shouldCompileIslandVue
			? tracePhase('compile/island-vue', () =>
					import('../build/compileVue').then((mod) =>
						mod.compileVue(
							islandVueSources,
							vueDir,
							hmr,
							styleTransformConfig
						)
					)
				)
			: {
					vueClientPaths: [...emptyStringArray]
				},
		shouldCompileIslandAngular
			? tracePhase('compile/island-angular', () =>
					import('../build/compileAngular').then((mod) =>
						mod.compileAngular(
							islandAngularSources,
							angularDir,
							hmr,
							styleTransformConfig
						)
					)
				)
			: {
					clientPaths: [...emptyStringArray]
				}
	]);

	const islandSvelteClientPathMap = new Map<string, string>();
	for (let idx = 0; idx < islandSvelteSources.length; idx++) {
		const sourcePath = islandSvelteSources[idx];
		const clientPath = islandSvelteClientPaths[idx];
		if (!sourcePath || !clientPath) continue;
		islandSvelteClientPathMap.set(resolve(sourcePath), clientPath);
	}

	const islandVueClientPathMap = new Map<string, string>();
	for (let idx = 0; idx < islandVueSources.length; idx++) {
		const sourcePath = islandVueSources[idx];
		const clientPath = islandVueClientPaths[idx];
		if (!sourcePath || !clientPath) continue;
		islandVueClientPathMap.set(resolve(sourcePath), clientPath);
	}

	const islandAngularClientPathMap = new Map<string, string>();
	for (let idx = 0; idx < islandAngularSources.length; idx++) {
		const sourcePath = islandAngularSources[idx];
		const clientPath = islandAngularClientPaths[idx];
		if (!sourcePath || !clientPath) continue;
		islandAngularClientPathMap.set(resolve(sourcePath), clientPath);
	}

	// Compile convention files (error/loading/not-found) into build/conventions/
	// so runtime SSR can import them from the build output instead of source.
	const reactConventionSources = collectConventionSourceFiles(
		conventionsMap.react
	);
	const svelteConventionSources = collectConventionSourceFiles(
		conventionsMap.svelte
	);
	const vueConventionSources = collectConventionSourceFiles(
		conventionsMap.vue
	);
	const angularConventionSources = collectConventionSourceFiles(
		conventionsMap.angular
	);
	let conventionOutputPaths: string[] = [];

	if (
		reactConventionSources.length > 0 ||
		svelteConventionSources.length > 0 ||
		vueConventionSources.length > 0 ||
		angularConventionSources.length > 0
	) {
		const compileReactConventions = async () => {
			if (reactConventionSources.length === 0) return emptyStringArray;

			const destDir = join(buildPath, 'conventions', 'react');
			rmSync(destDir, { force: true, recursive: true });
			mkdirSync(destDir, { recursive: true });

			const destPaths: string[] = [];
			for (let idx = 0; idx < reactConventionSources.length; idx++) {
				const source = reactConventionSources[idx];
				if (!source) continue;

				const result = await bunBuild({
					entrypoints: [source],
					format: 'esm',
					jsx: { development: false },
					minify: !isDev,
					naming: `${idx}-[name].[ext]`,
					outdir: destDir,
					plugins: [stylePreprocessorPlugin],
					root: dirname(source),
					target: 'bun',
					throw: false,
					tsconfig: './tsconfig.json'
				});
				if (!result.success) {
					outputLogs(result.logs);
					throw new Error(
						`Failed to compile React convention: ${source}`
					);
				}

				const output = result.outputs.find((artifact) =>
					artifact.path.endsWith('.js')
				);
				if (!output)
					throw new Error(
						`React convention did not emit JavaScript: ${source}`
					);

				destPaths.push(output.path);
			}

			return destPaths;
		};

		const [
			reactConvPaths,
			svelteConvResult,
			vueConvResult,
			angularConvResult
		] = await Promise.all([
			tracePhase('compile/convention-react', compileReactConventions),
			svelteConventionSources.length > 0 && svelteDir
				? tracePhase('compile/convention-svelte', () =>
						import('../build/compileSvelte').then((mod) =>
							mod.compileSvelte(
								svelteConventionSources,
								svelteDir,
								new Map(),
								false,
								styleTransformConfig
							)
						)
					)
				: { svelteServerPaths: emptyStringArray },
			vueConventionSources.length > 0 && vueDir
				? tracePhase('compile/convention-vue', () =>
						import('../build/compileVue').then((mod) =>
							mod.compileVue(
								vueConventionSources,
								vueDir,
								false,
								styleTransformConfig
							)
						)
					)
				: { vueServerPaths: emptyStringArray },
			angularConventionSources.length > 0 && angularDir
				? tracePhase('compile/convention-angular', () =>
						import('../build/compileAngular').then((mod) =>
							mod.compileAngular(
								angularConventionSources,
								angularDir,
								hmr,
								styleTransformConfig
							)
						)
					)
				: { serverPaths: emptyStringArray }
		]);

		// Bundle compiled convention files to build/conventions/{framework}/
		// so compiled executables can import them from the materialized runtime
		// directory without relying on the app's node_modules tree.
		const bundleConventionFiles = async (
			framework: string,
			compiledPaths: string[]
		) => {
			const destDir = join(buildPath, 'conventions', framework);
			rmSync(destDir, { force: true, recursive: true });
			mkdirSync(destDir, { recursive: true });
			const destPaths: string[] = [];
			for (let idx = 0; idx < compiledPaths.length; idx++) {
				const compiledPath = compiledPaths[idx];
				if (!compiledPath) continue;
				const name = basename(compiledPath).replace(/\.[^.]+$/, '');
				const result = await bunBuild({
					entrypoints: [compiledPath],
					format: 'esm',
					minify: !isDev,
					naming: `${idx}-${name}.[ext]`,
					outdir: destDir,
					splitting: false,
					target: 'bun',
					throw: false
				});
				if (!result.success) {
					outputLogs(result.logs);
					throw new Error(
						`Failed to bundle ${framework} convention: ${compiledPath}`
					);
				}
				const output = result.outputs.find((artifact) =>
					artifact.path.endsWith('.js')
				);
				if (!output)
					throw new Error(
						`${framework} convention did not emit JavaScript: ${compiledPath}`
					);

				destPaths.push(output.path);
			}

			return destPaths;
		};

		const [svelteDests, vueDests, angularDests] = await Promise.all([
			tracePhase('bundle/convention-svelte', () =>
				bundleConventionFiles(
					'svelte',
					svelteConvResult.svelteServerPaths
				)
			),
			tracePhase('bundle/convention-vue', () =>
				bundleConventionFiles('vue', vueConvResult.vueServerPaths)
			),
			tracePhase('bundle/convention-angular', () =>
				bundleConventionFiles('angular', angularConvResult.serverPaths)
			)
		]);
		conventionOutputPaths = [
			...reactConvPaths,
			...svelteDests,
			...vueDests,
			...angularDests
		];

		updateConventionCompiledPaths(
			conventionsMap.react,
			reactConventionSources,
			reactConvPaths
		);
		updateConventionCompiledPaths(
			conventionsMap.svelte,
			svelteConventionSources,
			svelteDests
		);
		updateConventionCompiledPaths(
			conventionsMap.vue,
			vueConventionSources,
			vueDests
		);
		updateConventionCompiledPaths(
			conventionsMap.angular,
			angularConventionSources,
			angularDests
		);
	}

	const serverEntryPoints = [
		...svelteServerPaths,
		...vueServerPaths,
		...angularServerPaths
	];
	const reactClientEntryPoints = [...reactEntries];
	// Scan for files referenced by new URL('./path', import.meta.url) — these
	// are regular files (e.g. workers) that Bun.build won't follow automatically.
	const urlReferencedFiles = await urlReferencedFilesPromise;

	// In dev (hmr=true), serve Angular page modules via moduleServer
	// instead of bundling them. The hydration TS files written by
	// `compileAngular` are served at `/@src/<rel>` URLs and the
	// browser fetches each Angular module on demand — matching how
	// React/Svelte work in dev. Bundling in production / non-HMR
	// builds is unchanged.
	const skipAngularClientBundle = hmr && angularClientPaths.length > 0;
	const nonReactClientEntryPoints = [
		...svelteIndexPaths,
		...svelteClientPaths,
		...htmlEntries,
		...vueIndexPaths,
		...vueClientPaths,
		...(skipAngularClientBundle ? [] : angularClientPaths),
		...(islandBootstrapPath ? [islandBootstrapPath] : []),
		...urlReferencedFiles
	];
	const islandEntryResult = islandBuildInfo
		? await tracePhase('islands/client-entry-generation', () =>
				generateIslandEntryPoints({
					buildInfo: islandBuildInfo,
					buildPath,
					clientPathMaps: {
						angular: islandAngularClientPathMap,
						svelte: islandSvelteClientPathMap,
						vue: islandVueClientPathMap
					}
				})
			)
		: {
				entries: [],
				generatedRoot: join(buildPath, '_island_entries')
			};
	const islandClientEntryPoints = islandEntryResult.entries.map(
		(entry) => entry.entryPath
	);

	if (
		serverEntryPoints.length === 0 &&
		reactClientEntryPoints.length === 0 &&
		nonReactClientEntryPoints.length === 0 &&
		islandClientEntryPoints.length === 0 &&
		htmxDir === undefined &&
		htmlDir === undefined
	) {
		logWarn('No entry points found, manifest will be empty');
		sendTelemetryEvent('build:empty', {
			configuredDirs: {
				angular: Boolean(angularDir),
				html: Boolean(htmlDir),
				htmx: Boolean(htmxDir),
				react: Boolean(reactDir),
				svelte: Boolean(svelteDir),
				vue: Boolean(vueDir)
			},
			filteredEntries: {
				angular: angularEntries.length,
				html: htmlEntries.length,
				react: reactEntries.length,
				svelte: svelteEntries.length,
				vue: vueEntries.length
			},
			frameworks: frameworkNames,
			incremental: Boolean(isIncremental),
			mode: mode ?? (isDev ? 'development' : 'production'),
			scannedEntries: {
				angular: allAngularEntries.length,
				html: allHtmlEntries.length,
				react: allReactEntries.length,
				svelte: allSvelteEntries.length,
				vue: allVueEntries.length
			}
		});
		writeBuildTrace(buildPath);

		return {};
	}

	// In dev, add the _refresh entry to force React into a shared chunk
	// so HMR can re-import component entries without duplicating React.
	// Only add when React entries exist (i.e. React files actually changed
	// or this is a full build) to avoid producing stale React outputs
	// during non-React incremental rebuilds.
	if (hmr && reactIndexesPath && reactClientEntryPoints.length > 0) {
		const refreshEntry = join(reactIndexesPath, '_refresh.tsx');
		if (!reactClientEntryPoints.includes(refreshEntry))
			reactClientEntryPoints.push(refreshEntry);
	}

	// In dev mode, externalize React so imports get rewritten to stable
	// vendor file paths. If module-level state was lost (Bun --hot
	// re-evaluated devVendorPaths.ts), recompute and restore it.
	let vendorPaths = getDevVendorPaths();
	if (!vendorPaths && hmr && reactDir) {
		const { computeVendorPaths } = await import(
			'../build/buildReactVendor'
		);
		vendorPaths = computeVendorPaths();
		setDevVendorPaths(vendorPaths);
	}
	// Production: dev pre-builds the React vendor in devBuild.ts; prod has no
	// equivalent, so build it here and populate the path map. Without this,
	// React is never externalized and gets inlined into the host page AND every
	// React island bundle — two+ React copies → "Invalid hook call" / null
	// dispatcher when islands hydrate. Mirrors the Angular prod block below.
	if (!hmr && reactDir) {
		const { buildReactVendor, computeVendorPaths } = await import(
			'../build/buildReactVendor'
		);
		await buildReactVendor(buildPath);
		vendorPaths = computeVendorPaths();
		setDevVendorPaths(vendorPaths);
	}
	let angularVendorPaths = getAngularVendorPaths();
	if (!angularVendorPaths && hmr && angularDir) {
		const { computeAngularVendorPaths } = await import(
			'../build/buildAngularVendor'
		);
		angularVendorPaths = computeAngularVendorPaths(
			globalThis.__angularVendorSpecifiers
		);
		setAngularVendorPaths(angularVendorPaths);
	}
	// §1.1 — in HMR mode, intentionally leave `angularServerVendorPaths`
	// empty. `devBuild.ts` no longer builds the Angular server vendor
	// (the build was the load-bearing source of two `@angular/core`
	// instances co-existing in SSR after an HMR cycle). Without these
	// paths, the page-bundle rewrite block below is a no-op, server
	// bundles keep bare `@angular/*` specifiers, and Bun's node_modules
	// resolution gives one canonical instance per process.
	let angularServerVendorPaths = hmr
		? undefined
		: getAngularServerVendorPaths();
	// Production: dev pre-builds vendor in devBuild.ts; prod has no
	// equivalent step, so do it here. Build the linked Angular vendor
	// (browser + server targets) ahead of the main bundle pass so
	// `rewriteImports` has a populated path map. linkerJitMode=false
	// because user pages are AOT-compiled in prod and have
	// `ɵcmp.dependencies` baked in — keeping NgModule.declarations
	// would just be dead bytes.
	if (!hmr && angularDir) {
		const angularSourceDirs = [
			angularDir,
			reactDir,
			svelteDir,
			vueDir,
			htmlDir,
			htmxDir
		].filter((dir): dir is string => Boolean(dir));
		const {
			buildAngularVendor,
			buildAngularServerVendor,
			computeAngularVendorPathsAsync,
			computeAngularServerVendorPathsAsync
		} = await import('../build/buildAngularVendor');

		[angularVendorPaths, angularServerVendorPaths] = await Promise.all([
			computeAngularVendorPathsAsync(
				angularSourceDirs,
				/* linkerJitMode */ false
			),
			computeAngularServerVendorPathsAsync(
				buildPath,
				angularSourceDirs,
				/* linkerJitMode */ false
			)
		]);
		setAngularVendorPaths(angularVendorPaths);
		setAngularServerVendorPaths(angularServerVendorPaths);

		await Promise.all([
			buildAngularVendor(
				buildPath,
				angularSourceDirs,
				/* linkerJitMode */ false,
				[]
			),
			buildAngularServerVendor(
				buildPath,
				angularSourceDirs,
				/* linkerJitMode */ false
			)
		]);

		// `getAngularDeps` reads ABSOLUTE_BUILD_DIR to find the server
		// vendor at runtime. The CLI's `start` script sets this for the
		// production server, but if `build()` is invoked directly without
		// going through `start`, we still want the runtime to find vendor.
		process.env.ABSOLUTE_BUILD_DIR ??= buildPath;
	}
	let vueVendorPaths = getVueVendorPaths();
	if (!vueVendorPaths && hmr && vueDir) {
		const { computeVueVendorPaths } = await import(
			'../build/buildVueVendor'
		);
		vueVendorPaths = computeVueVendorPaths();
		setVueVendorPaths(vueVendorPaths);
	}
	// Production: build the Vue vendor so Vue is externalized + shared (one
	// instance across host page and Vue island bundles), not inlined per bundle.
	if (!hmr && vueDir) {
		const { buildVueVendor, computeVueVendorPaths } = await import(
			'../build/buildVueVendor'
		);
		await buildVueVendor(buildPath);
		vueVendorPaths = computeVueVendorPaths();
		setVueVendorPaths(vueVendorPaths);
	}
	let svelteVendorPaths = getSvelteVendorPaths();
	if (!svelteVendorPaths && hmr && svelteDir) {
		const { computeSvelteVendorPaths } = await import(
			'../build/buildSvelteVendor'
		);
		svelteVendorPaths = computeSvelteVendorPaths();
		setSvelteVendorPaths(svelteVendorPaths);
	}
	// Production: build the Svelte vendor so the Svelte client runtime is
	// externalized + shared (one instance across host page and Svelte island
	// bundles), not inlined — duplicated runtimes break Svelte 5 hydration
	// (`Cannot read properties of null (reading 'nodes')`).
	if (!hmr && svelteDir) {
		const { buildSvelteVendor, computeSvelteVendorPaths } = await import(
			'../build/buildSvelteVendor'
		);
		await buildSvelteVendor(buildPath);
		svelteVendorPaths = computeSvelteVendorPaths();
		setSvelteVendorPaths(svelteVendorPaths);
	}
	const depVendorPaths =
		hmr && globalThis.__depVendorPaths ? globalThis.__depVendorPaths : {};
	const reactExternalPaths: Record<string, string> = {
		...(vendorPaths ?? {}),
		...depVendorPaths
	};
	const nonReactExternalPaths: Record<string, string> = {
		...reactExternalPaths,
		...(angularVendorPaths ?? {}),
		...(vueVendorPaths ?? {}),
		...(svelteVendorPaths ?? {})
	};
	// Third-party Angular libraries (ngx-markdown, ngx-datatable, etc.)
	// ship `ɵɵngDeclare*` partials and need linker processing — they're
	// detected by `collectTransitiveAngularSpecs` and get a vendor/server
	// bundle built for them. Without externalizing here, Bun INLINES them
	// into every page bundle that imports `provideMarkdown` (or similar)
	// transitively. The SSR runtime then loads two distinct copies — one
	// inlined per page, one from node_modules (or the server vendor) —
	// and the duplicate `MarkdownComponent` class definitions produce
	// NG0912 component-id collisions on first SSR render. Externalizing
	// keeps the bare specifier intact so dev resolves through
	// node_modules (one canonical instance) and prod's `rewriteImports`
	// retargets to the linked server-vendor file.
	// Shared with the dev bundle rebuilds (src/build/serverExternals.ts) so a
	// single-framework project's SSR rebuild doesn't fail resolving the other
	// frameworks' runtimes — see that module for per-entry rationale.
	const serverBuildExternals = buildServerBundleExternals(angularVendorPaths);

	// Svelte-aware libraries (svelte-routing, svelte-spa-router, etc.)
	// publish their entry only under custom export conditions like
	// `"svelte"` and `"main"`. Without honoring those, Bun's resolver
	// fails outright with "Could not resolve" because none of the
	// standard import/require/default conditions match. Vite's official
	// Svelte plugin sets these for the same reason — it's the convention
	// the Svelte ecosystem expects.
	const svelteResolveConditions: string[] | undefined = svelteDir
		? ['svelte', 'main']
		: undefined;

	const htmlScriptPlugin = hmr
		? createHTMLScriptHMRPlugin(htmlDir, htmxDir)
		: undefined;
	// Bun's reactFastRefresh injects a bare `react-refresh/runtime` import into
	// every component. It's externalized via reactExternalPaths (built as a
	// stable vendor file from the copy shipped in this package) and rewritten
	// to its /react/vendor URL by rewriteReactImports — the same treatment as
	// react/react-dom. Resolve-and-bundling it instead (with splitting:true)
	// hoisted the runtime's `register` binding into a shared chunk that Bun
	// failed to re-link on incremental rebuilds, leaving a dangling
	// `export_register` reference that crashed the app (issue #38).
	const reactBuildConfig: Parameters<typeof bunBuild>[0] | undefined =
		reactClientEntryPoints.length > 0
			? mergeBunBuildConfig(
					{
						entrypoints: reactClientEntryPoints,
						...(Object.keys(reactExternalPaths).length > 0
							? { external: Object.keys(reactExternalPaths) }
							: {}),
						format: 'esm',
						minify: !isDev,
						naming: `[dir]/[name].[hash].[ext]`,
						outdir: buildPath,
						...(hmr
							? {
									jsx: { development: true },
									reactFastRefresh: true
								}
							: {}),
						plugins: [
							stylePreprocessorPlugin,
							...islandRegistryPlugins
						],
						root: clientRoot,
						sourcemap: resolveClientSourcemap(sourcemaps, isDev),
						splitting: true,
						target: 'browser',
						throw: false
					},
					resolveBunBuildOverride(bunBuildConfig, 'reactClient')
				)
			: undefined;

	// Remove old hashed indexes before bundling so stale files
	// from previous builds don't accumulate in dist/.
	if (reactDir && reactClientEntryPoints.length > 0) {
		rmSync(join(buildPath, 'react', 'generated', 'indexes'), {
			force: true,
			recursive: true
		});
	}
	if (angularDir && angularClientPaths.length > 0) {
		rmSync(join(buildPath, 'angular', 'indexes'), {
			force: true,
			recursive: true
		});
	}
	if (islandClientEntryPoints.length > 0) {
		rmSync(join(buildPath, 'islands'), {
			force: true,
			recursive: true
		});
	}

	// Run all 4 Bun.build passes in parallel — they write to different
	// directories and have independent entry points.
	const [
		serverResult,
		reactClientResult,
		nonReactClientResult,
		islandClientResult,
		globalCssResult,
		vueCssResult
	] = await Promise.all([
		serverEntryPoints.length > 0
			? tracePhase('bun/server', () =>
					bunBuild(
						mergeBunBuildConfig(
							{
								conditions: svelteResolveConditions,
								entrypoints: serverEntryPoints,
								external: serverBuildExternals,
								format: 'esm',
								naming: `[dir]/[name].[hash].[ext]`,
								outdir: serverOutDir,
								plugins: [
									stylePreprocessorPlugin,
									...islandRegistryPlugins,
									...(serverOutDir
										? [
												createExternalAssetPlugin(
													serverOutDir,
													allFrameworkDirs
												)
											]
										: [])
								],
								root: serverRoot,
								// Dev-only inline sourcemaps + post-build
								// chain so SSR error stacks map back to
								// the .vue source (see
								// docs/BUN_SOURCEMAP_CHAIN_BUG.md).
								sourcemap: isDev ? 'inline' : 'none',
								target: 'bun',
								throw: false,
								tsconfig: './tsconfig.json'
							},
							resolveBunBuildOverride(bunBuildConfig, 'server')
						)
					)
				)
			: undefined,
		reactBuildConfig
			? tracePhase('bun/react-client', () => bunBuild(reactBuildConfig))
			: undefined,
		nonReactClientEntryPoints.length > 0
			? tracePhase('bun/non-react-client', () =>
					bunBuild(
						mergeBunBuildConfig(
							{
								conditions: svelteResolveConditions,
								define: vueDirectory
									? vueFeatureFlags
									: undefined,
								entrypoints: nonReactClientEntryPoints,
								external: Object.keys(nonReactExternalPaths),
								format: 'esm',
								minify: !isDev,
								naming: `[dir]/[name].[hash].[ext]`,
								outdir: buildPath,
								plugins: [
									stylePreprocessorPlugin,
									...islandRegistryPlugins,
									...(angularDir && hmr
										? [
												createAngularHmrInjectionPlugin(
													{
														generatedAngularRoot:
															getFrameworkGeneratedDir(
																'angular',
																projectRoot
															),
														projectRoot,
														userAngularRoot:
															angularDir
													}
												)
											]
										: []),
									...(angularDir
										? [createAngularLinkerPlugin(hmr)]
										: []),
									...(htmlScriptPlugin
										? [htmlScriptPlugin]
										: [])
								],
								root: clientRoot,
								sourcemap: resolveClientSourcemap(
									sourcemaps,
									isDev
								),
								splitting: !isDev,
								target: 'browser',
								throw: false,
								tsconfig: './tsconfig.json'
							},
							resolveBunBuildOverride(
								bunBuildConfig,
								'nonReactClient'
							)
						)
					)
				)
			: undefined,
		islandClientEntryPoints.length > 0
			? tracePhase('bun/island-client', () =>
					bunBuild(
						mergeBunBuildConfig(
							{
								conditions: svelteResolveConditions,
								define: vueDirectory
									? vueFeatureFlags
									: undefined,
								entrypoints: islandClientEntryPoints,
								external: Object.keys(nonReactExternalPaths),
								format: 'esm',
								minify: !isDev,
								naming: `[dir]/[name].[hash].[ext]`,
								outdir: buildPath,
								plugins: [
									stylePreprocessorPlugin,
									...islandRegistryPlugins,
									...(angularDir && hmr
										? [
												createAngularHmrInjectionPlugin(
													{
														generatedAngularRoot:
															getFrameworkGeneratedDir(
																'angular',
																projectRoot
															),
														projectRoot,
														userAngularRoot:
															angularDir
													}
												)
											]
										: []),
									...(angularDir
										? [createAngularLinkerPlugin(hmr)]
										: [])
								],
								root: islandEntryResult.generatedRoot,
								sourcemap: resolveClientSourcemap(
									sourcemaps,
									isDev
								),
								splitting: !isDev,
								target: 'browser',
								throw: false,
								tsconfig: './tsconfig.json'
							},
							resolveBunBuildOverride(
								bunBuildConfig,
								'islandClient'
							)
						)
					)
				)
			: undefined,
		globalCssEntries.length > 0
			? tracePhase('bun/global-css', () =>
					bunBuild(
						mergeBunBuildConfig(
							{
								entrypoints: globalCssEntries,
								naming: `[dir]/[name].[hash].[ext]`,
								outdir: stylesDir
									? join(buildPath, basename(stylesDir))
									: buildPath,
								plugins: [stylePreprocessorPlugin],
								root: stylesDir || clientRoot,
								target: 'browser',
								throw: false
							},
							resolveBunBuildOverride(bunBuildConfig, 'globalCss')
						)
					)
				)
			: undefined,
		vueCssPaths.length > 0
			? tracePhase('bun/vue-css', () =>
					bunBuild(
						mergeBunBuildConfig(
							{
								entrypoints: vueCssPaths,
								naming: `[name].[hash].[ext]`,
								outdir: join(
									buildPath,
									assetsPath
										? basename(assetsPath)
										: 'assets',
									'css'
								),
								target: 'browser',
								throw: false
							},
							resolveBunBuildOverride(bunBuildConfig, 'vueCss')
						)
					)
				)
			: undefined
	]);

	const serverLogs = serverResult?.logs ?? [];
	const serverOutputs = serverResult?.outputs ?? [];

	/* Dev resilience: a single failed bundling pass (a bad CSS `@import`,
	 * a broken component import) must NOT abort the whole build. In dev we
	 * record the failure and keep going, so `generateManifest` below still
	 * runs over the passes that succeeded (merged over `baseManifest`) and
	 * every unaffected route keeps serving. Gated on `hmr` (injectHMR),
	 * which is set by every dev/HMR build path and never by `absolute
	 * build` — so production keeps the original fail-hard behaviour
	 * (`extractBuildError` throws / exits) regardless of ambient
	 * NODE_ENV. */
	const passErrors: BuildPassError[] = [];
	const handlePassFailure = (
		logs: (BuildMessage | ResolveMessage)[],
		pass: string,
		label: string
	) => {
		if (hmr) {
			passErrors.push(
				buildPassError(logs, pass, label, frameworkNames, isIncremental)
			);

			return;
		}
		extractBuildError(
			logs,
			pass,
			label,
			frameworkNames,
			isIncremental,
			throwOnError
		);
	};

	// Dev-mode sourcemap chain (docs/BUN_SOURCEMAP_CHAIN_BUG.md). Bun.build
	// emits a map from the final bundle back to the per-framework
	// intermediate (e.g. compileVue's .absolutejs/generated/vue/.../X.js
	// which carries its own inline map to the .vue source) but does
	// not chain through it. Post-process here so SSR error stacks
	// resolve to the user's source files.
	if (isDev && serverResult?.success) {
		const { chainBundleInlineSourcemap } = await import(
			'../build/chainInlineSourcemaps'
		);
		for (const out of serverOutputs) {
			if (out.path.endsWith('.js')) chainBundleInlineSourcemap(out.path);
		}
	}

	// Production EXTERNAL client source maps (`sourcemaps: 'external'`): chain
	// each `.js.map` back to original `.vue`/`.ts` source, then move it OUT of
	// the served build dir into a private `sourcemaps/` dir and strip the
	// `sourceMappingURL` comment from the shipped JS — so source is NOT publicly
	// exposed, and a server-side symbolicator (e.g. @absolutejs/errors) reads the
	// private maps keyed by the JS file's basename.
	if (!isDev && resolveClientSourcemap(sourcemaps, isDev) === 'external') {
		const { chainExternalSourcemap } = await import(
			'../build/chainInlineSourcemaps'
		);
		const sourcemapDir = join(projectRoot, 'sourcemaps');
		mkdirSync(sourcemapDir, { recursive: true });
		const mapFiles = (
			readdirSync(buildPath, { recursive: true }) as string[]
		)
			.filter(
				(entry) =>
					entry.endsWith('.js.map') && !entry.includes('node_modules')
			)
			.map((entry) => join(buildPath, entry));
		for (const mapPath of mapFiles) {
			chainExternalSourcemap(mapPath);
			renameSync(mapPath, join(sourcemapDir, basename(mapPath)));
			const jsPath = mapPath.slice(0, -4); // drop ".map"
			try {
				const js = readFileSync(jsPath, 'utf-8').replace(
					/\n?\/\/# sourceMappingURL=[^\n]*\s*$/,
					'\n'
				);
				writeFileSync(jsPath, js);
			} catch {
				// best-effort — a missing/locked JS file must not fail the build
			}
		}
		console.info(
			`[absolute] sourcemaps: chained + privatized ${mapFiles.length} client maps → ${relative(projectRoot, sourcemapDir)}/`
		);
	}

	if (serverResult && !serverResult.success && serverLogs.length > 0) {
		handlePassFailure(serverLogs, 'server', 'Server');
	}

	const reactClientLogs = reactClientResult?.logs ?? [];
	const reactClientOutputs = reactClientResult?.outputs ?? [];

	if (
		reactClientResult &&
		!reactClientResult.success &&
		reactClientLogs.length > 0
	) {
		handlePassFailure(reactClientLogs, 'react-client', 'React client');
	}

	const reactClientOutputPaths = reactClientOutputs.map(
		(artifact) => artifact.path
	);

	// Use reactExternalPaths (vendorPaths + depVendorPaths) so dep
	// specifiers that were externalized at bundle time get rewritten to
	// their /vendor/*.js paths. Using just `vendorPaths` here leaks bare
	// specifiers like `@react-spring/web` into page bundles, which the
	// browser cannot resolve and which crashes hydration.
	if (
		Object.keys(reactExternalPaths).length > 0 &&
		reactClientOutputPaths.length > 0
	) {
		await tracePhase('postprocess/react-imports', () =>
			rewriteReactImports(reactClientOutputPaths, reactExternalPaths)
		);
	}

	if (hmr && reactClientOutputPaths.length > 0) {
		await tracePhase('postprocess/react-refresh-globals', () =>
			patchRefreshGlobals(reactClientOutputPaths)
		);
	}

	const nonReactClientLogs = nonReactClientResult?.logs ?? [];
	const nonReactClientOutputs = nonReactClientResult?.outputs ?? [];
	const nonReactClientOutputPaths = nonReactClientOutputs.map(
		(artifact) => artifact.path
	);
	const islandClientLogs = islandClientResult?.logs ?? [];
	const islandClientOutputs = islandClientResult?.outputs ?? [];
	const islandClientOutputPaths = islandClientOutputs.map(
		(artifact) => artifact.path
	);

	if (
		Object.keys(nonReactExternalPaths).length > 0 &&
		nonReactClientOutputPaths.length > 0
	) {
		await tracePhase('postprocess/non-react-react-imports', () =>
			rewriteReactImports(
				nonReactClientOutputPaths,
				nonReactExternalPaths
			)
		);
	}
	if (hmr && nonReactClientOutputPaths.length > 0) {
		await tracePhase('postprocess/non-react-refresh-globals', () =>
			patchRefreshGlobals(nonReactClientOutputPaths)
		);
	}

	if (
		Object.keys(nonReactExternalPaths).length > 0 &&
		islandClientOutputPaths.length > 0
	) {
		await tracePhase('postprocess/island-react-imports', () =>
			rewriteReactImports(islandClientOutputPaths, nonReactExternalPaths)
		);
	}
	if (hmr && islandClientOutputPaths.length > 0) {
		await tracePhase('postprocess/island-refresh-globals', () =>
			patchRefreshGlobals(islandClientOutputPaths)
		);
	}

	if (
		nonReactClientResult &&
		!nonReactClientResult.success &&
		nonReactClientLogs.length > 0
	) {
		handlePassFailure(
			nonReactClientLogs,
			'non-react-client',
			'Non-React client'
		);
	}
	if (
		islandClientResult &&
		!islandClientResult.success &&
		islandClientLogs.length > 0
	) {
		handlePassFailure(islandClientLogs, 'island-client', 'Island client');
	}

	// Post-process: rewrite bare Angular/Vue specifiers to vendor paths.
	const allNonReactVendorPaths: Record<string, string> = {
		...depVendorPaths,
		...(angularVendorPaths ?? {}),
		...(vueVendorPaths ?? {}),
		...(svelteVendorPaths ?? {})
	};
	const allIslandVendorPaths: Record<string, string> = {
		...reactExternalPaths,
		...allNonReactVendorPaths
	};
	if (
		nonReactClientOutputs.length > 0 &&
		Object.keys(allNonReactVendorPaths).length > 0
	) {
		const { rewriteBuildOutputs } = await import(
			'../build/rewriteImportsPlugin'
		);
		await tracePhase('postprocess/non-react-vendor-imports', () =>
			rewriteBuildOutputs(nonReactClientOutputs, allNonReactVendorPaths)
		);
	}
	if (
		islandClientOutputs.length > 0 &&
		Object.keys(allIslandVendorPaths).length > 0
	) {
		const { rewriteBuildOutputs } = await import(
			'../build/rewriteImportsPlugin'
		);
		await tracePhase('postprocess/island-vendor-imports', () =>
			rewriteBuildOutputs(islandClientOutputs, allIslandVendorPaths)
		);
	}

	// Production guardrail: every browser chunk (app entries, shared/split
	// chunks, and the React vendor files) must have its bare React specifiers
	// vendored. Fail the build if any survive — a single un-vendored chunk
	// silently breaks hydration. Skipped for HMR/dev builds (served via the
	// dev module server, not as static chunks).
	if (!hmr) {
		const reactVendorDir = join(buildPath, 'react', 'vendor');
		const vendorChunkPaths = existsSync(reactVendorDir)
			? [
					...new Glob('**/*.js').scanSync({
						absolute: true,
						cwd: reactVendorDir
					})
				]
			: [];
		verifyBrowserImportsVendored([
			...reactClientOutputPaths,
			...nonReactClientOutputPaths,
			...islandClientOutputPaths,
			...vendorChunkPaths
		]);
	}

	// Server-side: rewrite bare @angular/* specifiers in SSR outputs to
	// absolute paths under build/angular/vendor/server/. This ensures every
	// page bundle (and the absolutejs runtime, via getAngularDeps) imports
	// the same fully-linked Angular files — single instance, no NG0201,
	// no @angular/compiler at runtime in AOT mode. Skipped in dev mode
	// (§1.1) — `angularServerVendorPaths` is not set there, so the gate
	// is false and this whole block is a no-op.
	if (
		serverOutputs.length > 0 &&
		angularServerVendorPaths &&
		Object.keys(angularServerVendorPaths).length > 0
	) {
		const { rewriteBuildOutputsWith } = await import(
			'../build/rewriteImportsPlugin'
		);
		// Each server output sits at a different depth in the build tree
		// (e.g. `angular/generated/pages/X.js` vs `vue/generated/server/.../X.js`).
		// Compute the relative path from each artifact to the vendor file so
		// the rewritten import survives the build dir being relocated at
		// runtime — required by the `compile` standalone executable, which
		// extracts all assets into a fresh runtimeDir on first launch.
		await tracePhase('postprocess/server-angular-vendor-imports', () =>
			rewriteBuildOutputsWith(serverOutputs, (artifact) => {
				const fileDir = dirname(artifact.path);
				const relativePaths: Record<string, string> = {};
				for (const [specifier, absolute] of Object.entries(
					angularServerVendorPaths
				)) {
					const rel = relative(fileDir, absolute);
					relativePaths[specifier] = rel.startsWith('.')
						? rel
						: `./${rel}`;
				}

				return relativePaths;
			})
		);
	}

	// §3.4 SSR Angular core multi-instance guardrail.
	// `NG0203` — and all the "two `currentInjector` globals" weirdness
	// it implies — happens when the SSR runtime ends up with more
	// than one `@angular/core` module instance. The §1.1 fix pinned
	// to a single resolution path; this check is the build-time
	// guardrail so a future regression (a new vendor build, a config
	// drift, mixed strategies) fails CI instead of producing
	// `Failed to resolve injector` at request time.
	if (serverOutputs.length > 0 && angularDir) {
		await tracePhase('verify/angular-core-uniqueness', () =>
			verifyAngularCoreUniqueness(
				serverOutputs,
				serverOutDir,
				throwOnError
			)
		);
	}

	const cssLogs: (BuildMessage | ResolveMessage)[] = [
		...(globalCssResult?.logs ?? []),
		...(vueCssResult?.logs ?? [])
	];
	const cssOutputs: BuildArtifact[] = [
		...(globalCssResult?.outputs ?? []),
		...(vueCssResult?.outputs ?? [])
	];

	if (
		globalCssResult &&
		!globalCssResult.success &&
		globalCssResult.logs.length > 0
	) {
		handlePassFailure(globalCssResult.logs, 'global-css', 'Global CSS');
	}

	if (vueCssResult && !vueCssResult.success && vueCssResult.logs.length > 0) {
		handlePassFailure(vueCssResult.logs, 'vue-css', 'Vue CSS');
	}

	// In dev mode, rewrite new URL('./path', import.meta.url) in all bundled
	// client outputs to /@src/ URLs so workers resolve through the module server.
	// In prod mode, rewrite to the hashed output path from the build.
	const allClientOutputPaths = [
		...reactClientOutputPaths,
		...nonReactClientOutputs.map((artifact) => artifact.path)
	];
	if (urlReferencedFiles.length > 0) {
		const urlFileMap = buildUrlFileMap(
			urlReferencedFiles,
			hmr,
			projectRoot,
			buildPath,
			nonReactClientOutputs
		);
		await tracePhase('postprocess/url-references', () =>
			rewriteUrlReferences(allClientOutputPaths, urlFileMap)
		);
	}

	// In dev mode, inject composable state tracking into Vue bundled output
	// so the first HMR cycle can preserve ref values.
	const vueOutputPaths = nonReactClientOutputs
		.map((artifact) => artifact.path)
		.filter((path) => path.includes('/vue/'));
	if (hmr && vueDirectory) {
		await tracePhase('postprocess/vue-hmr', () =>
			vueOutputPaths.forEach((outputPath) =>
				injectVueComposableTracking(outputPath, projectRoot)
			)
		);
	}

	const allLogs = [
		...serverLogs,
		...reactClientLogs,
		...nonReactClientLogs,
		...islandClientLogs,
		...cssLogs
	];
	outputLogs(allLogs);

	const manifest: Record<string, string> = {
		...(options?.baseManifest || {}),
		...generateManifest(
			[
				...serverOutputs,
				...reactClientOutputs,
				...nonReactClientOutputs,
				...islandClientOutputs,
				...cssOutputs
			],
			buildPath
		)
	};

	// Server pages (Svelte, Vue, Angular) need absolute file paths for SSR
	// import(), not web-relative paths. Overwrite with absolute paths.
	// Skip co-emitted CSS — it already has its own `${Page}BundledCSS` key
	// from generateManifest and would otherwise clobber the JS entry.
	for (const artifact of serverOutputs) {
		if (extname(artifact.path) !== '.js') continue;
		const fileWithHash = basename(artifact.path);
		const [baseName] = fileWithHash.split(`.${artifact.hash}.`);
		if (!baseName) continue;
		manifest[toPascal(baseName)] = artifact.path;
	}

	// Per-page CSS sidecar: for each SSR JS output, write a sibling .css
	// containing the page's compiled scoped styles. handle*PageRequest
	// reads `${pagePath}.css` (sibling next to the SSR JS) at render time
	// and inlines it as a <style> block in the SSR head, so scoped style
	// rules are available on first paint and the chrome doesn't render
	// unstyled for a frame before the client bundle finishes loading.
	//
	// The Bun.build CSS passes already hash the CSS to `<name>.<hash>.ext`
	// in `build/assets/css/`. The naming convention links each output to
	// its origin via the `[name]` segment: Vue's CSS inputs are kebab-cased
	// `<page>-compiled.css` (from compileVue.ts:545), so the SSR JS output
	// `<PageName>.<jshash>.js` maps to CSS `<kebab(PageName)>-compiled.<csshash>.css`.
	// We don't rely on URL routing — the handler reads the FS sibling.
	// Extract the basename without hash. Bun's [name].[hash].[ext] outputs
	// have a `hash` field on the artifact, but it can be null for chunks/
	// non-entry outputs — fall back to a regex peel of `<name>.<8+chars>.<ext>`.
	const stripHash = (fileBase: string, hash: string | null) => {
		if (hash) {
			const tag = `.${hash}.`;
			const idx = fileBase.indexOf(tag);
			if (idx > 0) return fileBase.slice(0, idx);
		}
		// Generic peel: `<name>.<hash>.<ext>` where hash is 8+ alphanumerics
		const m = fileBase.match(/^(.+)\.[a-z0-9]{8,}\.[^.]+$/i);

		return m ? m[1] : null;
	};

	const cssByName = new Map<string, BuildArtifact>();
	for (const artifact of cssOutputs) {
		if (extname(artifact.path) !== '.css') continue;
		const cssName = stripHash(basename(artifact.path), artifact.hash);
		if (cssName) cssByName.set(cssName, artifact);
	}
	const fsPromises = await import('node:fs/promises');
	const siblingCssPaths: string[] = [];
	const serverJsByPascalName = new Map<string, BuildArtifact>();
	await Promise.all(
		serverOutputs.map(async (artifact) => {
			if (extname(artifact.path) !== '.js') return;
			const pascalName = stripHash(
				basename(artifact.path),
				artifact.hash
			);
			if (!pascalName) return;
			serverJsByPascalName.set(pascalName, artifact);
			// Vue convention. (Svelte/Angular wire up matching names from
			// their own compilers — extend this lookup as those land.)
			const cssArtifact = cssByName.get(
				`${toKebab(pascalName)}-compiled`
			);
			if (!cssArtifact) return;
			const siblingCssPath = artifact.path.replace(/\.js$/, '.css');
			await fsPromises.copyFile(cssArtifact.path, siblingCssPath);
			siblingCssPaths.push(siblingCssPath);
			// Register in manifest so cleanStaleAssets (in devBuild)
			// recognises the sibling as a live build artifact and doesn't
			// remove it on the next walk. Without this, hashed files not
			// in the manifest get unlinked.
			manifest[`${pascalName}Css`] = siblingCssPath;
		})
	);

	// SPA child-route CSS side manifest. For every Vue page that
	// registers `export const routes = defineRoutes([...])`, write
	// `<pagePath>.spa.json` next to the SSR JS. The Vue page handler
	// reads this at request time, matches the URL against the listed
	// routes, and inlines the matched child route's compiled CSS too —
	// otherwise a fresh nav to e.g. /portal/dashboard paints Dashboard's
	// markup unstyled until the lazy chunk lands. See
	// `utils/spaRouteCss.ts` for the runtime side.
	const spaSideManifestPaths: string[] = [];
	const spaManifestEntries =
		vueSpaRoutesBySource && vueSpaRoutesBySource.size > 0
			? await writeSpaSideManifests(
					vueSpaRoutesBySource,
					(pascalName) => serverJsByPascalName.get(pascalName)?.path
				)
			: {};
	for (const [key, sideManifestPath] of Object.entries(spaManifestEntries)) {
		manifest[key] = sideManifestPath;
		spaSideManifestPaths.push(sideManifestPath);
	}

	// Ember server bundles bypass the central serverOutputs pass — they're
	// already self-contained Bun.build outputs from compileEmber (which uses
	// its own resolver plugin to handle @ember/*/@glimmer/* transitive
	// imports — see EMBER_BANDAID #1). Register them in the manifest by
	// hand so `asset(manifest, 'EmberExample')` resolves to the absolute
	// path of the compiled server bundle.
	for (const serverPath of emberServerPaths) {
		const fileBase = basename(serverPath, '.js');
		manifest[toPascal(fileBase)] = serverPath;
	}

	// In dev with `skipAngularClientBundle`, no Bun.build artifact has
	// produced `${PageName}Index` manifest entries for Angular pages.
	// Populate them with `/@src/...` URLs to the on-disk hydration TS
	// files so the SSR `<script>import(URL)</script>` injection points
	// at moduleServer-served code that's always fresh.
	if (skipAngularClientBundle) {
		for (const clientPath of angularClientPaths) {
			const fileBase = basename(clientPath, '.js');
			const relFromCwd = relative(projectRoot, clientPath).replace(
				/\\/g,
				'/'
			);
			manifest[`${toPascal(fileBase)}Index`] = `/@src/${relFromCwd}`;
		}
	}

	/* Same-basename pages across `htmlDirectory` and `htmxDirectory`
	 * (or two pages with the same name within one of those dirs)
	 * collide in `manifest[<basename>]`. Whichever pipeline runs
	 * last wins; the other page silently 404s when its route
	 * looks the key up. Emit a clear build-time warning so the
	 * user can rename one. Doesn't break the build — keeping the
	 * last-writer-wins behaviour preserves existing setups that
	 * intentionally rely on it. */
	const warnManifestKeyCollision = (
		key: string,
		previousPath: string,
		nextPath: string
	) => {
		logWarn(
			`Manifest key collision: "${key}" was "${previousPath}" and will be overwritten by "${nextPath}". Rename one of the conflicting pages so each manifest key is unique across html/htmx (and other framework) directories.`
		);
	};

	const shouldCopyHtmx =
		!isIncremental ||
		normalizedIncrementalFiles?.some(
			(f) => f.includes('/htmx/') && f.endsWith('.html')
		);

	// Update asset paths if CSS changed (even if HTML files didn't change)
	const shouldUpdateHtmlAssetPaths =
		!isIncremental ||
		normalizedIncrementalFiles?.some(
			(f) =>
				f.includes('/html/') && (f.endsWith('.html') || isStylePath(f))
		);
	const shouldUpdateHtmxAssetPaths =
		!isIncremental ||
		normalizedIncrementalFiles?.some(
			(f) =>
				f.includes('/htmx/') && (f.endsWith('.html') || isStylePath(f))
		);

	// Await the HMR client bundle that was started before the compile phase
	const hmrClientBundle = hmrClientBundlePromise
		? await hmrClientBundlePromise
		: null;

	const injectHMRIntoHTMLFile = (filePath: string, framework: string) => {
		if (!hmrClientBundle) return;
		let html = readFileSync(filePath, 'utf-8');
		if (html.includes('data-hmr-client')) return;
		const tag =
			`<script>window.__HMR_FRAMEWORK__="${framework}";</script>` +
			`<script data-hmr-client>${hmrClientBundle}</script>`;
		const bodyClose = /<\/body\s*>/i.exec(html);
		html = bodyClose
			? html.slice(0, bodyClose.index) + tag + html.slice(bodyClose.index)
			: html + tag;
		writeFileSync(filePath, html);
	};

	// HTML + HTMX post-processing run in parallel (independent directories)
	const processHtmlPages = async () => {
		if (!(htmlDir && htmlPagesPath)) return;
		const outputHtmlPages = isSingle
			? join(buildPath, 'pages')
			: join(buildPath, basename(htmlDir), 'pages');

		mkdirSync(outputHtmlPages, { recursive: true });
		cpSync(htmlPagesPath, outputHtmlPages, {
			force: true,
			recursive: true
		});

		// Update asset paths if HTML files changed OR CSS changed
		if (shouldUpdateHtmlAssetPaths) {
			await updateAssetPaths(manifest, outputHtmlPages);
			await optimizeHtmlImages(outputHtmlPages);
		}

		// Add HTML pages to manifest (absolute paths for Bun.file())
		const htmlPageFiles = await scanEntryPoints(outputHtmlPages, '*.html');
		await transformStaticPagesWithIslands(
			islandRegistryPath,
			htmlPageFiles
		);
		for (const htmlFile of htmlPageFiles) {
			if (hmr) injectHMRIntoHTMLFile(htmlFile, 'html');
			const fileName = basename(htmlFile, '.html');
			if (manifest[fileName] && manifest[fileName] !== htmlFile) {
				warnManifestKeyCollision(
					fileName,
					manifest[fileName],
					htmlFile
				);
			}
			manifest[fileName] = htmlFile;
		}
	};

	const processHtmxPages = async () => {
		if (!(htmxDir && htmxPagesPath)) return;
		const outputHtmxPages = isSingle
			? join(buildPath, 'pages')
			: join(buildPath, basename(htmxDir), 'pages');

		mkdirSync(outputHtmxPages, { recursive: true });
		cpSync(htmxPagesPath, outputHtmxPages, {
			force: true,
			recursive: true
		});

		if (shouldCopyHtmx) {
			const htmxDestDir = isSingle
				? buildPath
				: join(buildPath, basename(htmxDir));
			copyHtmxVendor(htmxDir, htmxDestDir);
		}

		// Update asset paths if HTMX files changed OR CSS changed
		if (shouldUpdateHtmxAssetPaths) {
			await updateAssetPaths(manifest, outputHtmxPages);
			await optimizeHtmlImages(outputHtmxPages);
		}

		// Add HTMX pages to manifest (absolute paths for Bun.file())
		const htmxPageFiles = await scanEntryPoints(outputHtmxPages, '*.html');
		await transformStaticPagesWithIslands(
			islandRegistryPath,
			htmxPageFiles
		);
		for (const htmxFile of htmxPageFiles) {
			if (hmr) injectHMRIntoHTMLFile(htmxFile, 'htmx');
			const fileName = basename(htmxFile, '.html');
			if (manifest[fileName] && manifest[fileName] !== htmxFile) {
				warnManifestKeyCollision(
					fileName,
					manifest[fileName],
					htmxFile
				);
			}
			manifest[fileName] = htmxFile;
		}
	};

	await Promise.all([
		tracePhase('postprocess/html-pages', processHtmlPages),
		tracePhase('postprocess/htmx-pages', processHtmxPages)
	]);

	if (!isIncremental) {
		await tracePhase('cleanup/stale-outputs', () =>
			cleanStaleOutputs(buildPath, [
				...serverOutputs.map((a) => a.path),
				...reactClientOutputs.map((a) => a.path),
				...nonReactClientOutputs.map((a) => a.path),
				...islandClientOutputs.map((a) => a.path),
				...cssOutputs.map((a) => a.path),
				...siblingCssPaths,
				...spaSideManifestPaths,
				...conventionOutputPaths
			])
		);
	}

	// In dev mode, copy source indexes to build dir before cleanup.
	// Rewrite relative page imports to absolute /@src/ paths since
	// the indexes are moved from src/frontend/indexes/ to build/_src_indexes/
	if (hmr) {
		await tracePhase('dev/copy-indexes', () =>
			copyDevIndexes({
				buildPath,
				reactIndexesPath,
				reactPagesPath,
				svelteDir,
				svelteEntries,
				sveltePagesPath,
				vueDir,
				vueEntries,
				vuePagesPath
			})
		);
	}

	await tracePhase('cleanup/generated', () =>
		cleanup({
			angularDir,
			preserveAngularGenerated: skipAngularClientBundle,
			reactDir,
			svelteDir,
			vueDir
		})
	);

	if (!isIncremental) {
		globalThis.__hmrBuildDuration = performance.now() - buildStart;
	}

	sendTelemetryEvent('build:complete', {
		durationMs: Math.round(performance.now() - buildStart),
		frameworks: frameworkNames,
		mode: mode ?? (isDev ? 'development' : 'production')
	});

	const [reactSpaHosts, svelteSpaHosts, vueSpaHosts, angularSpaHosts] =
		await Promise.all([
			reactDir
				? import('../react/staticAnalyzeSpaRoutes').then((module) =>
						module.analyzeReactSpaRoutes(reactDir)
					)
				: [],
			svelteDir
				? import('../svelte/staticAnalyzeSpaRoutes').then((module) =>
						module.analyzeSvelteSpaRoutes(svelteDir)
					)
				: [],
			vueDir
				? import('../vue/staticAnalyzeSpaRoutes').then((module) =>
						module.analyzeVueSpaRoutes(vueDir)
					)
				: [],
			angularDir
				? import('../angular/staticAnalyzeSpaRoutes').then((module) =>
						module.analyzeAngularSpaRoutes(angularDir)
					)
				: []
		]);
	const spaRouteHosts = [
		...reactSpaHosts.map((host) => ({
			...host,
			framework: 'react' as const
		})),
		...svelteSpaHosts.map((host) => ({
			...host,
			framework: 'svelte' as const
		})),
		...vueSpaHosts.map((host) => ({ ...host, framework: 'vue' as const })),
		...angularSpaHosts.map((host) => ({
			...host,
			framework: 'angular' as const
		}))
	];
	setSpaRouteManifest(spaRouteHosts);
	writeFileSync(
		join(buildPath, 'spa-routes.json'),
		JSON.stringify(spaRouteHosts, null, '\t')
	);

	// Skip manifest.json disk write during incremental (HMR) builds —
	// the in-memory manifest is authoritative and writing to disk on
	// every keystroke adds unnecessary I/O latency.
	if (isIncremental) {
		writeBuildTrace(buildPath);

		return {
			conventions: conventionsMap,
			errors: passErrors.length > 0 ? passErrors : undefined,
			manifest
		};
	}

	writeFileSync(
		join(buildPath, 'manifest.json'),
		JSON.stringify(manifest, null, '\t')
	);

	// Write convention files map (error/loading/not-found) if any exist
	if (Object.keys(conventionsMap).length > 0) {
		writeFileSync(
			join(buildPath, 'conventions.json'),
			JSON.stringify(conventionsMap, null, '\t')
		);
	}

	writeBuildTrace(buildPath);

	// Sitemap is a deterministic build artifact derived from a static
	// scan of project source for Elysia route registrations. Runs after
	// every other artifact is on disk so we can read the project tree in
	// a stable state; failure here is non-fatal (the build still
	// succeeds, we just log and move on).
	if (mode === 'production') {
		try {
			const routes = scanRouteRegistrations(projectRoot).map((r) => ({
				handlerSource: r.handlerSource,
				method: r.method,
				path: r.path
			}));
			const sitemapBaseUrl =
				sitemap?.baseUrl ?? `http://localhost:${DEFAULT_PORT}`;
			await generateSitemap(routes, sitemapBaseUrl, buildPath, sitemap, {
				angularDirectory,
				reactDirectory,
				svelteDirectory,
				vueDirectory
			});
		} catch (err) {
			console.warn('[sitemap] Build-time generation failed:', err);
		}
	}

	// Production builds don't need the persistent Tailwind compiler in
	// memory after the final write — release it. Dev / HMR builds keep
	// the compiler alive so subsequent rebuilds reuse its candidate cache.
	if (tailwind && mode === 'production') {
		disposeTailwindCompiler(tailwind.input);
	}

	return {
		conventions: conventionsMap,
		errors: passErrors.length > 0 ? passErrors : undefined,
		manifest
	};
};

export const build = async (config: BuildConfig) => {
	const projectRoot = cwd();
	const buildPath = validateSafePath(
		config.buildDirectory ?? 'build',
		projectRoot
	);

	return withBuildDirectoryLock(buildPath, () => buildUnlocked(config));
};
